import { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import {
  CloudDownloadOutlined,
  ContentCopyOutlined,
  DeleteOutline,
  FilterList,
  MoreVert,
  ZoomOutMap,
} from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import { useSearchParams } from 'react-router-dom';
import { ApiCreateDocumentGenerationJob } from 'api';
import type {
  CompanyDocuments,
  DocumentReferencesView,
  FirmDocumentCompany,
  FirmDocumentFilterOptionsResponse,
  FirmDocumentFolder,
  FirmDocumentFund,
  FirmDocumentMeasurementDateListItem,
  FirmDocumentReference,
  FirmDocumentSuggestion,
  FolderView,
  MeasurementDateName,
} from 'api';
import { DATE_ALIAS } from 'common/constants/general';
import { EXPORT_VIEWER_VALUE, LIMITED_VIEWER_VALUE } from 'common/constants/user';
import { useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import {
  FileTypeIcon,
  PrimaryNavigationMenu,
  PrimaryNavigationMenuAiIcon,
  PrimaryNavigationMenuItem,
  TERTIARY_NAVIGATION_ACTION_UNMOUNT_DELAY_MS,
  TertiaryNavigationSearchSuggestionPill,
  WorkspacePaneLayout,
} from 'components/ui';
import type { EntityListPanelFilterValue, TertiaryNavigationSearchSuggestion } from 'components/ui';
import { useCountdown, useDelayedCommitValue } from 'components/ui/hooks';
import {
  TERTIARY_NAVIGATION_QUERY_PARAMS,
  useTertiaryNavigationQueryParamState,
  useTertiaryNavigationRegistration,
} from 'context/FirmBaseLayoutContext';
import { useQueryParamValue } from 'hooks/useQueryParamValue';
import { useResponse } from 'services/hooks';
import { useGetMeasurementDatesByCompany } from 'services/hooks/companies';
import {
  FIRM_DOCUMENT_SUGGESTIONS_QUERY_MIN_SEARCH_LENGTH,
  useFirmDocumentActions,
  useFirmDocumentCompaniesQuery,
  useFirmDocumentFilterOptionsQuery,
  useFirmDocumentFoldersQuery,
  useFirmDocumentMeasurementDateDocumentsQuery,
  useFirmDocumentMeasurementDatesQuery,
  useFirmDocumentRequestedFilesQuery,
  useFirmDocumentsQuery,
  useFirmDocumentSuggestionsQuery,
} from 'services/hooks/documents';
import type { FirmDocumentDownloadTarget } from 'services/hooks/documents';
import { getNormalizedSearch } from 'services/hooks/documents/documentsQuery.utils';
import { PrismThemeProvider } from 'theme';
import { shortDate } from 'utilities';
import {
  AllCompaniesPanel,
  FirmDocumentAnalyzeDialog,
  FirmDocumentCopyDialog,
  FirmDocumentDeleteDialog,
  FirmDocumentDownloadDialog,
  FirmDocumentEditDialog,
  FirmDocumentFolderCreateDialog,
  FirmDocumentFolderDeleteDialog,
  FirmDocumentFolderEditDialog,
  FirmDocumentMoveDialog,
  FirmDocumentsFilterDialog,
  FirmDocumentUploadDialog,
  MeasurementDateDocumentsTree,
  MeasurementDatesAccordionPanel,
} from './components';
import type {
  FirmDocumentCopyTargetMeasurementDate,
  FirmDocumentEditDialogValues,
  FirmDocumentFileActionReference,
  FirmDocumentFolderCreateCompanyOption,
  FirmDocumentFolderCreateDialogValues,
  FirmDocumentFolderEditDialogValues,
  FirmDocumentFolderParentOption,
  FirmDocumentsFilterValue,
  FirmDocumentUploadDialogProgress,
  FirmDocumentUploadDialogValues,
} from './components';
import {
  FIRM_DOCUMENTS_DOCUMENT_ORDERING_VALUES,
  FIRM_DOCUMENTS_DOCUMENT_SOURCES,
  FIRM_DOCUMENTS_LABELS,
  FIRM_DOCUMENTS_PANE_WIDTHS,
  FIRM_DOCUMENTS_QUERY_PARAMS,
  FIRM_DOCUMENTS_SUPPORTED_REFERENCE_OPTIONS,
} from './FirmDocuments.constants';
import {
  FirmDocumentsBulkActionEndIcon,
  FirmDocumentsFilterActionBadge,
  FirmDocumentsFilterTooltip,
  FirmDocumentsRoot,
} from './FirmDocuments.styles';
import { FIRM_DOCUMENTS_TEST_IDS } from './FirmDocuments.testIds';
import { getFirmDocumentReferenceFileName } from './utilities';
import type { FirmDocumentTreeFolder } from './utilities';

type FirmLevelFolderView = FolderView & {
  companyId?: number | null;
  isCompanyGroup?: boolean;
  measurementDateId?: number | null;
};

type FirmDocumentMeasurementDate = FirmDocumentMeasurementDateListItem;

const EMPTY_COMPANIES: FirmDocumentCompany[] = [];
const EMPTY_DOCUMENTS: FirmDocumentReference[] = [];
const EMPTY_DOCUMENT_SUGGESTIONS: FirmDocumentSuggestion[] = [];
const EMPTY_DOCUMENT_ACTION_REFERENCES: FirmDocumentFileActionReference[] = [];
const EMPTY_FUNDS: FirmDocumentFund[] = [];
const EMPTY_MEASUREMENT_DATES: FirmDocumentMeasurementDate[] = [];
const ALL_DOCUMENTS_TAB_VALUE = 'all-documents';
const EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS: number[] = [];
const MEASUREMENT_DATE_DATE_QUERY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MEASUREMENT_DATE_ID_QUERY_PATTERN = /^\d+$/;
const ALL_DOCUMENTS_UNKNOWN_COMPANY_FOLDER_ID = -1_000_000_000;
const DOCUMENT_SEARCH_SUGGESTION_DELAY_MS = 250;
const DOCUMENTS_VIEW_QUERY_SCOPE_ALL_DOCUMENTS = 'all-documents';
const DOCUMENTS_VIEW_SCOPED_QUERY_PARAMS = [
  DATE_ALIAS,
  ...Object.values(FIRM_DOCUMENTS_QUERY_PARAMS),
] as const;
const UPLOAD_AUTO_CLOSE_SECONDS = 3;
const DOCUMENT_TRANSFER_COPY_FALLBACK_ERROR = 'The selected documents could not be copied.';
const DOCUMENT_TRANSFER_MOVE_FALLBACK_ERROR = 'The selected document could not be moved.';

interface PendingMeasurementDateSelection {
  checked: boolean;
  measurementDateId: number;
}

interface PendingMeasurementDateAddFolderAction {
  measurementDate: FirmDocumentMeasurementDate;
  measurementDateKey: string;
}

type FirmDocumentAnalyzeProcessingType =
  | ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE
  | ApiCreateDocumentGenerationJob.processing_type.FINANCIAL_MAPPING;

interface PendingAnalyzeAction {
  companyId: number;
  documentReferences: FirmDocumentFileActionReference[];
  processingType: FirmDocumentAnalyzeProcessingType;
}

interface PendingMoveAction {
  documentReference: FirmDocumentFileActionReference;
  targetCompanyId?: number | null;
  targetFolderId: number | null;
  targetLabel?: string | null;
  targetMeasurementDateId: number;
}

interface PendingFolderDeleteAction {
  folderId: number;
  folderName: string;
  mdDocumentsId: number;
}

interface PendingFolderCreateAction {
  companyOptions?: FirmDocumentFolderCreateCompanyOption[];
  initialCompanyId?: number | null;
  initialParentFolderId?: number | null;
  measurementDateLabel?: string | null;
  mdDocumentsId?: number | null;
  parentFolderId?: number | null;
  parentFolderName?: string | null;
  parentFolderOptions?: FirmDocumentFolderParentOption[];
  parentFolderOptionsByCompanyId?: Record<number, FirmDocumentFolderParentOption[]>;
  siblingFolderNames: string[];
  siblingFolderNamesByTargetKey?: Record<string, string[]>;
  targetCompanyName?: string | null;
}

interface PendingFolderEditAction {
  currentParentFolderId: number | null;
  folderId: number;
  folderName: string;
  mdDocumentsId: number;
  parentFolderOptions: FirmDocumentFolderParentOption[];
  siblingFolderNamesByTargetKey: Record<string, string[]>;
}

interface PendingDocumentUploadAction {
  initialCompanyId?: number | null;
  initialFolderId?: number | null;
  initialMeasurementDateDate?: string | null;
  initialMeasurementDateId?: number | null;
  isCompanyLocked?: boolean;
}

const getCompanyTabValue = (companySlug: string) => `company-${companySlug}`;

const getMeasurementDateKey = (measurementDate: Pick<FirmDocumentMeasurementDate, 'date' | 'id'>) =>
  String(measurementDate.id ?? measurementDate.date);

const getMeasurementDateDisplayLabel = (measurementDate: Pick<MeasurementDateName, 'date' | 'name'>) =>
  shortDate(measurementDate.date) ?? measurementDate.name;

const getSortedMeasurementDates = <T extends Pick<MeasurementDateName, 'date'>>(measurementDates: T[]) =>
  [...measurementDates].sort((dateA, dateB) => dateB.date.localeCompare(dateA.date));

const getSelectedMeasurementDate = (
  measurementDates: FirmDocumentMeasurementDate[],
  queryValue: string | null,
  preferredMeasurementDateId: number | null = null
) => {
  if (!measurementDates.length) {
    return null;
  }

  if (!queryValue) {
    return measurementDates[0];
  }

  const preferredMeasurementDate = preferredMeasurementDateId
    ? measurementDates.find(measurementDate =>
      measurementDate.id === preferredMeasurementDateId
        && [String(measurementDate.id), measurementDate.date].includes(queryValue)
    )
    : null;

  if (preferredMeasurementDate) {
    return preferredMeasurementDate;
  }

  const matchedMeasurementDate = measurementDates.find(measurementDate =>
    [String(measurementDate.id), measurementDate.date].includes(queryValue)
  );

  return matchedMeasurementDate ?? measurementDates[0];
};

const getTargetMeasurementDateId = (queryValue: string | null) => {
  if (!queryValue || !MEASUREMENT_DATE_ID_QUERY_PATTERN.test(queryValue)) {
    return null;
  }

  const measurementDateId = Number(queryValue);

  return measurementDateId > 0 ? measurementDateId : null;
};

const getTargetMeasurementDate = (queryValue: string | null) => {
  if (!queryValue || !MEASUREMENT_DATE_DATE_QUERY_PATTERN.test(queryValue)) {
    return null;
  }

  return queryValue;
};

const getFundIdFromFilterValue = (value: EntityListPanelFilterValue) => {
  if (value === null) {
    return null;
  }

  const fundId = Number(value);

  return Number.isNaN(fundId) ? null : fundId;
};

const getDocumentOrderingQueryValue = (value: string | null) => (
  FIRM_DOCUMENTS_DOCUMENT_ORDERING_VALUES.some(optionValue => optionValue === value) ? value : null
);

const getDocumentSourceQueryValue = (value: string | null) => (
  FIRM_DOCUMENTS_DOCUMENT_SOURCES.some(option => option.value === value) ? value : null
);

const getActiveDocumentFilterCount = ({
  fileType,
  referenceObjectType,
  source,
  uploadedBy,
}: FirmDocumentsFilterValue) => [
  fileType,
  referenceObjectType,
  source,
  uploadedBy,
].filter(Boolean).length;

const getDocumentOrderingSummaryLabel = (ordering: string | null) => {
  switch (ordering) {
    case 'filename':
      return 'Sort: Filename (A-Z)';
    case '-filename':
      return 'Sort: Filename (Z-A)';
    case 'uploaded_date':
      return 'Sort: Upload Date (Oldest First)';
    case '-uploaded_date':
      return 'Sort: Upload Date (Newest First)';
    default:
      return null;
  }
};

const getDocumentFilterOptionLabel = (
  options: FirmDocumentFilterOptionsResponse | undefined,
  optionType: keyof FirmDocumentFilterOptionsResponse,
  value: string | null
) => {
  if (!value) {
    return null;
  }

  return options?.[optionType]?.find(option => option.value === value)?.label ?? value;
};

const getActiveDocumentFilterLabels = (
  filterValue: FirmDocumentsFilterValue,
  options?: FirmDocumentFilterOptionsResponse
) => [
  getDocumentOrderingSummaryLabel(filterValue.ordering),
  filterValue.source
    ? `Source: ${getDocumentFilterOptionLabel(options, 'sources', filterValue.source)}`
    : null,
  filterValue.uploadedBy
    ? `Uploaded By: ${getDocumentFilterOptionLabel(options, 'uploaded_by', filterValue.uploadedBy)}`
    : null,
  filterValue.referenceObjectType
    ? `Reference Type: ${getDocumentFilterOptionLabel(options, 'reference_types', filterValue.referenceObjectType)}`
    : null,
  filterValue.fileType
    ? `File Type: ${(getDocumentFilterOptionLabel(options, 'file_types', filterValue.fileType) ?? filterValue.fileType).toUpperCase()}`
    : null,
].filter((label): label is string => Boolean(label));

const getDocumentSearchSuggestions = (
  suggestions: FirmDocumentSuggestion[],
  isCompanyDocumentsView: boolean
): TertiaryNavigationSearchSuggestion[] => suggestions.map(suggestion => ({
  inputValue: suggestion.full_filename,
  label: suggestion.full_filename,
  metadata: (
    <>
      {!isCompanyDocumentsView && (
        <TertiaryNavigationSearchSuggestionPill tone="brand" tooltipTitle={suggestion.company.name}>
          {suggestion.company.name}
        </TertiaryNavigationSearchSuggestionPill>
      )}
      <TertiaryNavigationSearchSuggestionPill tone="neutral">
        {getMeasurementDateDisplayLabel(suggestion.measurement_date)}
      </TertiaryNavigationSearchSuggestionPill>
    </>
  ),
  startIcon: <FileTypeIcon fileType={suggestion.file_type} />,
  value: String(suggestion.document_reference_id),
}));

const setQueryParam = (searchParams: URLSearchParams, key: string, value: string | null) => {
  if (value) {
    searchParams.set(key, value);
    return;
  }

  searchParams.delete(key);
};

const getDocumentsViewQueryScope = (companySlug?: string | null) => (
  companySlug ?? DOCUMENTS_VIEW_QUERY_SCOPE_ALL_DOCUMENTS
);

const getDocumentsViewScopedQuery = (searchParams: URLSearchParams) => {
  const scopedQueryParams = new URLSearchParams();

  DOCUMENTS_VIEW_SCOPED_QUERY_PARAMS.forEach(param => {
    const value = searchParams.get(param);

    if (value) {
      scopedQueryParams.set(param, value);
    }
  });

  return scopedQueryParams.toString();
};

const applyDocumentsViewScopedQuery = ({
  fallbackDate,
  searchParams,
  scopedQuery,
}: {
  fallbackDate: string | null;
  searchParams: URLSearchParams;
  scopedQuery?: string;
}) => {
  const nextSearchParams = new URLSearchParams(searchParams);

  DOCUMENTS_VIEW_SCOPED_QUERY_PARAMS.forEach(param => nextSearchParams.delete(param));

  if (scopedQuery !== undefined) {
    new URLSearchParams(scopedQuery).forEach((value, key) => nextSearchParams.set(key, value));
  } else if (fallbackDate) {
    nextSearchParams.set(DATE_ALIAS, fallbackDate);
  }

  return nextSearchParams;
};

const getFirmDocumentDownloadTarget = (
  documentReference: FirmDocumentFileActionReference,
  firmId?: number | null
): FirmDocumentDownloadTarget | null => {
  const fileId = documentReference.file.id;

  if (!fileId) {
    return null;
  }

  return {
    fileId,
    fileName: getFirmDocumentReferenceFileName(documentReference),
    firmId,
  };
};

const isFirmDocumentDownloadTarget = (
  target: FirmDocumentDownloadTarget | null
): target is FirmDocumentDownloadTarget => Boolean(target);

const getDocumentReferenceCompanyId = (documentReference: FirmDocumentFileActionReference) =>
  (documentReference as FirmDocumentReference).company?.id ?? null;

const getDocumentReferenceCompanyName = (documentReference: FirmDocumentFileActionReference) =>
  (documentReference as FirmDocumentReference).company?.name ?? FIRM_DOCUMENTS_LABELS.unknownCompany;

const getDocumentReferenceMeasurementDateId = (
  documentReference: FirmDocumentFileActionReference,
  fallbackMeasurementDateId?: number | null
) => (documentReference as FirmDocumentReference).measurement_date?.id ?? fallbackMeasurementDateId ?? null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const getFirstErrorText = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(getFirstErrorText).find(Boolean) ?? null;
  }

  if (isRecord(value)) {
    return Object.values(value).map(getFirstErrorText).find(Boolean) ?? null;
  }

  return null;
};

const getApiErrorBody = (error: unknown) => {
  if (!isRecord(error)) {
    return null;
  }

  if ('body' in error) {
    return error.body;
  }

  const { response } = error;

  if (isRecord(response) && 'data' in response) {
    return response.data;
  }

  return null;
};

const getApiErrorMessage = (error: unknown): string | null => {
  const body = getApiErrorBody(error);

  if (!isRecord(body)) {
    return null;
  }

  return getFirstErrorText(body.document_reference_ids) ?? getFirstErrorText(body);
};

interface DocumentTransferConflictDetail {
  filename: string | null;
  reason: string | null;
}

const getDocumentTransferConflictDetails = (error: unknown): DocumentTransferConflictDetail[] => {
  const body = getApiErrorBody(error);

  if (!isRecord(body) || !Array.isArray(body.document_reference_conflicts)) {
    return [];
  }

  return body.document_reference_conflicts.flatMap(conflict => {
    if (!isRecord(conflict)) {
      return [];
    }

    const filename = getFirstErrorText(conflict.filename);
    const reason = getFirstErrorText(conflict.reason);

    if (!filename && !reason) {
      return [];
    }

    return [{ filename, reason }];
  });
};

const getDocumentTransferConflictReason = (reason: string | null) => {
  if (reason === 'This document already exists in the destination.') {
    return 'This document already exists in the selected destination.';
  }

  if (reason === 'A document with this name already exists in this location.') {
    return 'A document with this name already exists in the selected destination.';
  }

  return reason ?? 'This document conflicts with the selected destination.';
};

const getDocumentTransferConflictLine = ({ filename, reason }: DocumentTransferConflictDetail) => {
  const conflictReason = getDocumentTransferConflictReason(reason);

  return filename ? `${filename} - ${conflictReason}` : conflictReason;
};

const getDocumentTransferErrorMessage = ({
  actionPastTense,
  defaultMessage,
  documentCount,
  error,
}: {
  actionPastTense: 'copied' | 'moved';
  defaultMessage: string;
  documentCount: number;
  error: unknown;
}) => {
  const conflictDetails = getDocumentTransferConflictDetails(error);
  const actionMessage = documentCount === 1
    ? `The document was not ${actionPastTense}.`
    : `No documents were ${actionPastTense}.`;

  if (conflictDetails.length > 0) {
    const conflictReason = documentCount === 1
      ? `The document was not ${actionPastTense} because it conflicts with the selected destination.`
      : `No documents were ${actionPastTense} because ${conflictDetails.length} selected documents conflict with the selected destination.`;

    return [
      conflictReason,
      ...conflictDetails.map(getDocumentTransferConflictLine),
    ].join('\n');
  }

  const reason = getApiErrorMessage(error) ?? defaultMessage;

  return [
    reason,
    actionMessage,
  ].join('\n');
};

const getFolderActionTarget = (folder: FirmDocumentTreeFolder): PendingFolderDeleteAction | null => {
  if (folder.isCompanyGroup || !folder.md_documents) {
    return null;
  }

  return {
    folderId: folder.id,
    folderName: folder.name,
    mdDocumentsId: folder.md_documents,
  };
};

const getFolderParentFolderId = (folder: FirmDocumentTreeFolder) => {
  const parentFolderId = folder.parent_folder?.id;

  return parentFolderId && parentFolderId > 0 ? parentFolderId : null;
};

const getMeasurementDateDocumentsId = (documents: CompanyDocuments | null | undefined) =>
  documents?.files?.find(documentReference => documentReference.md_documents)?.md_documents
    ?? documents?.folders.find(folder => folder.md_documents)?.md_documents
    ?? null;

const getRootFolderNames = (documents: CompanyDocuments | null | undefined) => (
  documents?.folders
    .filter(folder => !folder.parent_folder?.id)
    .map(folder => folder.name)
    ?? []
);

const getSubFolderNames = (folder: FirmDocumentTreeFolder) =>
  folder.subFolders.map(subFolder => subFolder.name);

const getFolderCompanyName = (
  folder: FirmDocumentTreeFolder,
  documents: CompanyDocuments | null | undefined
) => {
  if (folder.isCompanyGroup) {
    return folder.name;
  }

  const foldersById = new Map(
    (documents?.folders ?? [])
      .filter((candidateFolder): candidateFolder is FirmLevelFolderView & { id: number } => (
        typeof candidateFolder.id === 'number'
      ))
      .map(candidateFolder => [candidateFolder.id, candidateFolder])
  );
  let currentParentId = folder.parent_folder?.id ?? null;
  let currentParentName = folder.parent_folder?.name ?? null;
  const visitedFolderIds = new Set<number>();

  while (currentParentId && !visitedFolderIds.has(currentParentId)) {
    visitedFolderIds.add(currentParentId);

    const parentFolder = foldersById.get(currentParentId);

    if (!parentFolder) {
      return currentParentId < 0 ? currentParentName : null;
    }

    if (parentFolder.isCompanyGroup) {
      return parentFolder.name;
    }

    currentParentId = parentFolder.parent_folder?.id ?? null;
    currentParentName = parentFolder.parent_folder?.name ?? null;
  }

  return null;
};

const getFolderCompanyId = (
  folder: FirmDocumentTreeFolder,
  documents: CompanyDocuments | null | undefined
) => {
  if (folder.isCompanyGroup) {
    return folder.companyId ?? null;
  }

  if (folder.companyId) {
    return folder.companyId;
  }

  const foldersById = new Map(
    (documents?.folders ?? [])
      .filter((candidateFolder): candidateFolder is FirmLevelFolderView & { id: number } => (
        typeof candidateFolder.id === 'number'
      ))
      .map(candidateFolder => [candidateFolder.id, candidateFolder])
  );
  let currentParentId = folder.parent_folder?.id ?? null;
  const visitedFolderIds = new Set<number>();

  while (currentParentId && !visitedFolderIds.has(currentParentId)) {
    visitedFolderIds.add(currentParentId);

    const parentFolder = foldersById.get(currentParentId);

    if (!parentFolder) {
      return null;
    }

    if (parentFolder.isCompanyGroup) {
      return parentFolder.companyId ?? null;
    }

    currentParentId = parentFolder.parent_folder?.id ?? null;
  }

  return null;
};

const getFolderCreateTargetKey = (mdDocumentsId: number | null | undefined, parentFolderId: number | null) =>
  `${mdDocumentsId ?? 'missing'}:${parentFolderId ?? 'root'}`;

const getFolderCreateParentFolderId = (folder: FolderView | FirmDocumentTreeFolder) => {
  const parentFolderId = folder.parent_folder?.id;

  return parentFolderId && parentFolderId > 0 ? parentFolderId : null;
};

const getFoldersForMeasurementDateDocuments = (
  documents: CompanyDocuments | null | undefined,
  mdDocumentsId: number | null | undefined
) => (
  mdDocumentsId
    ? documents?.folders.filter(folder => {
      const firmFolder = folder as FirmLevelFolderView;

      return !firmFolder.isCompanyGroup && firmFolder.md_documents === mdDocumentsId;
    }) ?? []
    : []
);

const ROOT_FOLDER_PARENT_OPTION: FirmDocumentFolderParentOption = { id: null, label: 'No Folder' };

const getFolderPathLabel = (
  folder: FolderView & { id: number },
  foldersById: Map<number, FolderView & { id: number }>,
  visitedFolderIds = new Set<number>()
): string => {
  if (visitedFolderIds.has(folder.id)) {
    return folder.name;
  }

  visitedFolderIds.add(folder.id);

  const parentFolderId = folder.parent_folder?.id;

  if (!parentFolderId) {
    return folder.name;
  }

  if (parentFolderId < 0) {
    return folder.name;
  }

  const parentFolder = foldersById.get(parentFolderId);
  const parentFolderPath = parentFolder
    ? getFolderPathLabel(parentFolder, foldersById, visitedFolderIds)
    : folder.parent_folder.name;

  return parentFolderPath ? `${parentFolderPath} > ${folder.name}` : folder.name;
};

const getFolderParentOption = (
  folder: FolderView & { id: number },
  foldersById: Map<number, FolderView & { id: number }>
): FirmDocumentFolderParentOption => {
  const folderPathLabel = getFolderPathLabel(folder, foldersById);

  return {
    id: folder.id,
    label: folderPathLabel,
    searchText: `${folder.name} ${folderPathLabel}`,
  };
};

const getFolderCreateParentOptions = (
  documents: CompanyDocuments | null | undefined,
  mdDocumentsId: number | null | undefined
): FirmDocumentFolderParentOption[] => {
  const folders = getFoldersForMeasurementDateDocuments(documents, mdDocumentsId)
    .filter((folder): folder is FolderView & { id: number } => typeof folder.id === 'number');
  const foldersById = new Map(folders.map(folder => [folder.id, folder]));

  return [
    ROOT_FOLDER_PARENT_OPTION,
    ...folders.map(folder => getFolderParentOption(folder, foldersById)),
  ];
};

const getFolderCreateSiblingFolderNamesByTargetKey = (
  documents: CompanyDocuments | null | undefined,
  mdDocumentsIds: Array<number | null | undefined>,
  excludedFolderId?: number | null
) => {
  const targetKeys = new Set(mdDocumentsIds.filter((id): id is number => typeof id === 'number'));

  return (documents?.folders ?? []).reduce<Record<string, string[]>>((accumulator, folder) => {
    const firmFolder = folder as FirmLevelFolderView;
    const mdDocumentsId = firmFolder.md_documents;

    if (
      !mdDocumentsId
      || !targetKeys.has(mdDocumentsId)
      || firmFolder.isCompanyGroup
      || folder.id === excludedFolderId
    ) {
      return accumulator;
    }

    const targetKey = getFolderCreateTargetKey(mdDocumentsId, getFolderCreateParentFolderId(folder));
    accumulator[targetKey] = [...(accumulator[targetKey] ?? []), folder.name];

    return accumulator;
  }, {});
};

const getFolderCreateCompanyOptions = (
  documents: CompanyDocuments | null | undefined,
  ordering?: string | null
): FirmDocumentFolderCreateCompanyOption[] => (
  (documents?.folders ?? [])
    .map(folder => folder as FirmLevelFolderView)
    .filter((folder): folder is FirmLevelFolderView & { companyId: number; md_documents: number } => (
      Boolean(folder.isCompanyGroup)
        && typeof folder.companyId === 'number'
        && typeof folder.md_documents === 'number'
    ))
    .map(folder => ({
      id: folder.companyId,
      label: folder.name,
      mdDocumentsId: folder.md_documents,
    }))
    .sort((companyA, companyB) => (
      ordering === '-filename'
        ? companyB.label.localeCompare(companyA.label, undefined, { sensitivity: 'base' })
        : companyA.label.localeCompare(companyB.label, undefined, { sensitivity: 'base' })
    ))
);

const getDescendantFolderIds = (folders: FolderView[], folderId: number) => {
  const childFoldersByParentId = new Map<number, number[]>();
  const descendantIds = new Set<number>();

  folders.forEach(folder => {
    const parentFolderId = folder.parent_folder?.id;

    if (!parentFolderId || typeof folder.id !== 'number') {
      return;
    }

    childFoldersByParentId.set(parentFolderId, [
      ...(childFoldersByParentId.get(parentFolderId) ?? []),
      folder.id,
    ]);
  });

  const pendingFolderIds = [...(childFoldersByParentId.get(folderId) ?? [])];

  while (pendingFolderIds.length) {
    const descendantFolderId = pendingFolderIds.shift();

    if (typeof descendantFolderId === 'number' && !descendantIds.has(descendantFolderId)) {
      descendantIds.add(descendantFolderId);
      pendingFolderIds.push(...(childFoldersByParentId.get(descendantFolderId) ?? []));
    }
  }

  return descendantIds;
};

const getFolderParentOptions = (
  documents: CompanyDocuments | null | undefined,
  folder: FirmDocumentTreeFolder
): FirmDocumentFolderParentOption[] => {
  const mdDocumentsId = folder.md_documents;

  if (!mdDocumentsId) {
    return [ROOT_FOLDER_PARENT_OPTION];
  }

  const folders = documents?.folders.filter(candidateFolder => candidateFolder.md_documents === mdDocumentsId) ?? [];
  const excludedFolderIds = getDescendantFolderIds(folders, folder.id);
  excludedFolderIds.add(folder.id);
  const selectableFolders = folders.filter((candidateFolder): candidateFolder is FolderView & { id: number } => (
    typeof candidateFolder.id === 'number' && !excludedFolderIds.has(candidateFolder.id)
  ));
  const foldersById = new Map(
    folders
      .filter((candidateFolder): candidateFolder is FolderView & { id: number } => typeof candidateFolder.id === 'number')
      .map(candidateFolder => [candidateFolder.id, candidateFolder])
  );

  return [
    ROOT_FOLDER_PARENT_OPTION,
    ...selectableFolders.map(candidateFolder => getFolderParentOption(candidateFolder, foldersById)),
  ];
};

const getAnalyzeActionLabel = (processingType: FirmDocumentAnalyzeProcessingType) => (
  processingType === ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE
    ? FIRM_DOCUMENTS_LABELS.analyzeCapTable
    : FIRM_DOCUMENTS_LABELS.analyzeFinancials
);

const getAllDocumentsCompanyFolderId = (companyId?: number | null) => (
  companyId ? ALL_DOCUMENTS_UNKNOWN_COMPANY_FOLDER_ID - companyId : ALL_DOCUMENTS_UNKNOWN_COMPANY_FOLDER_ID
);

const addAllDocumentsCompanyFolder = (
  foldersById: Map<number, FirmLevelFolderView>,
  {
    companyId,
    companyName,
    mdDocumentsId,
    measurementDateId,
  }: {
    companyId?: number | null;
    companyName: string;
    mdDocumentsId?: number | null;
    measurementDateId?: number | null;
  }
) => {
  const companyFolderId = getAllDocumentsCompanyFolderId(companyId);

  if (foldersById.has(companyFolderId)) {
    return companyFolderId;
  }

  foldersById.set(companyFolderId, {
    companyId,
    id: companyFolderId,
    isCompanyGroup: true,
    md_documents: mdDocumentsId ?? undefined,
    measurementDateId,
    name: companyName,
    parent_folder: { name: '' },
  });

  return companyFolderId;
};

const addFirmDocumentFolder = (
  foldersById: Map<number, FolderView>,
  folder: NonNullable<FirmDocumentReference['folder_details']>,
  parentFolder: FolderView['parent_folder'],
  measurementDateId?: number | null
) => {
  if (foldersById.has(folder.id)) {
    return;
  }

  foldersById.set(folder.id, {
    id: folder.id,
    md_documents: folder.md_documents ?? undefined,
    measurementDateId,
    name: folder.name,
    parent_folder: parentFolder,
  } as FirmLevelFolderView);
};

const addFirmDocumentFolderHierarchy = (
  foldersById: Map<number, FolderView>,
  folder: NonNullable<FirmDocumentReference['folder_details']>,
  rootParentFolder?: FolderView['parent_folder'],
  measurementDateId?: number | null
) => {
  const parentFolder = folder.parent_folder ?? null;

  if (parentFolder?.id) {
    addFirmDocumentFolder(foldersById, {
      id: parentFolder.id,
      md_documents: parentFolder.md_documents ?? undefined,
      name: parentFolder.name,
      parent_folder: undefined,
    }, rootParentFolder ?? { name: '' }, measurementDateId);
  }

  addFirmDocumentFolder(
    foldersById,
    folder,
    parentFolder?.id
      ? {
        id: parentFolder.id,
        md_documents: parentFolder.md_documents ?? undefined,
        name: parentFolder.name,
      }
      : rootParentFolder ?? { name: '' },
    measurementDateId
  );
};

const getFirmDocumentFolderView = (
  folder: FirmDocumentFolder,
  measurementDateId?: number | null
): FirmLevelFolderView => ({
  id: folder.id,
  md_documents: folder.md_documents ?? undefined,
  measurementDateId,
  name: folder.name,
  parent_folder: folder.parent_folder
    ? {
      id: folder.parent_folder.id,
      md_documents: folder.parent_folder.md_documents ?? undefined,
      name: folder.parent_folder.name,
    }
    : { name: '' },
});

const getFirmLevelMeasurementDateDocuments = (
  measurementDate: FirmDocumentMeasurementDate | null,
  documentReferences: FirmDocumentReference[],
  folders: FirmDocumentFolder[] = []
): CompanyDocuments | null => {
  if (!measurementDate) {
    return null;
  }

  const foldersById = new Map<number, FirmLevelFolderView>();
  folders.forEach(folder => {
    const companyName = folder.company?.name ?? FIRM_DOCUMENTS_LABELS.unknownCompany;
    const companyFolderId = addAllDocumentsCompanyFolder(foldersById, {
      companyId: folder.company?.id,
      companyName,
      mdDocumentsId: folder.md_documents,
      measurementDateId: folder.measurement_date?.id ?? measurementDate.id ?? null,
    });

    foldersById.set(folder.id, {
      id: folder.id,
      md_documents: folder.md_documents ?? undefined,
      measurementDateId: folder.measurement_date?.id ?? measurementDate.id ?? null,
      name: folder.name,
      parent_folder: folder.parent_folder?.id
        ? {
          id: folder.parent_folder.id,
          md_documents: folder.parent_folder.md_documents ?? undefined,
          name: folder.parent_folder.name,
        }
        : {
          id: companyFolderId,
          md_documents: folder.md_documents ?? undefined,
          name: companyName,
        },
    });
  });

  const files = documentReferences.map<DocumentReferencesView>(documentReference => {
    const companyName = documentReference.company?.name ?? FIRM_DOCUMENTS_LABELS.unknownCompany;
    const companyFolderId = addAllDocumentsCompanyFolder(foldersById, {
      companyId: documentReference.company?.id,
      companyName,
      mdDocumentsId: documentReference.md_documents,
      measurementDateId: documentReference.measurement_date?.id ?? measurementDate.id ?? null,
    });
    const documentMeasurementDateId = documentReference.measurement_date?.id ?? measurementDate.id ?? null;

    if (documentReference.folder_details?.id) {
      addFirmDocumentFolderHierarchy(foldersById, documentReference.folder_details, {
        id: companyFolderId,
        md_documents: documentReference.md_documents,
        name: companyName,
      }, documentMeasurementDateId);

      return documentReference;
    }

    return {
      ...documentReference,
      folder: companyFolderId,
    };
  });

  return {
    id: measurementDate.id,
    files,
    folders: [...foldersById.values()],
    measurement_date: measurementDate,
  };
};

const getCompanyLevelMeasurementDateDocuments = (
  measurementDate: FirmDocumentMeasurementDate | null,
  documentReferences: FirmDocumentReference[],
  folders: FirmDocumentFolder[] = []
): CompanyDocuments | null => {
  if (!measurementDate) {
    return null;
  }

  const foldersById = new Map<number, FolderView>();
  folders.forEach(folder => {
    foldersById.set(folder.id, getFirmDocumentFolderView(folder, measurementDate.id ?? null));
  });

  documentReferences.forEach(documentReference => {
    if (documentReference.folder_details?.id) {
      addFirmDocumentFolderHierarchy(
        foldersById,
        documentReference.folder_details,
        undefined,
        documentReference.measurement_date?.id ?? measurementDate.id ?? null
      );
    }
  });

  return {
    id: measurementDate.id,
    files: documentReferences,
    folders: [...foldersById.values()],
    measurement_date: measurementDate,
  };
};

const useRetainedDialogValue = <T,>(value: T, isOpen: boolean) => {
  const [retainedValue, setRetainedValue] = useState(value);

  // MUI keeps dialog children mounted during the close transition, so retain the last open payload.
  useEffect(() => {
    if (isOpen) {
      setRetainedValue(value);
    }
  }, [isOpen, value]);

  return isOpen ? value : retainedValue;
};

const FirmDocuments = () => {
  const [{ firmId }] = useStore() as UseStoreValues;
  const { currentFirmRole, isRestrictedUser, isViewOnlyUser } = useFirmScopedPermissions({ firmId });
  const [searchParams, setSearchParams] = useSearchParams();
  const measurementDateDocumentsByIdRef = useRef<Record<number, CompanyDocuments>>({});
  const previousFirmIdRef = useRef<number | null | undefined>(firmId);
  const documentsViewScopedQueryByScopeRef = useRef<Map<string, string>>(new Map());
  const [bulkActionsAnchorEl, setBulkActionsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [deletingDocumentReferences, setDeletingDocumentReferences] =
    useState<FirmDocumentFileActionReference[]>(EMPTY_DOCUMENT_ACTION_REFERENCES);
  const [deletingFolder, setDeletingFolder] = useState<PendingFolderDeleteAction | null>(null);
  const [creatingFolder, setCreatingFolder] = useState<PendingFolderCreateAction | null>(null);
  const [editingDocumentReference, setEditingDocumentReference] = useState<FirmDocumentFileActionReference | null>(null);
  const [editingFolder, setEditingFolder] = useState<PendingFolderEditAction | null>(null);
  const [uploadingDocumentContext, setUploadingDocumentContext] = useState<PendingDocumentUploadAction | null>(null);
  const [downloadingDocumentReferenceId, setDownloadingDocumentReferenceId] = useState<number | null>(null);
  const [downloadingDocumentReferences, setDownloadingDocumentReferences] =
    useState<FirmDocumentFileActionReference[]>(EMPTY_DOCUMENT_ACTION_REFERENCES);
  const [openedCompanyTabs, setOpenedCompanyTabs] = useState<FirmDocumentCompany[]>([]);
  const [copyingDocumentReferences, setCopyingDocumentReferences] =
    useState<FirmDocumentFileActionReference[]>(EMPTY_DOCUMENT_ACTION_REFERENCES);
  const [copyDialogErrorMessage, setCopyDialogErrorMessage] = useState<string | null>(null);
  const [initialCopyTargetMeasurementDateId, setInitialCopyTargetMeasurementDateId] = useState<number | null>(null);
  const [pendingAnalyzeAction, setPendingAnalyzeAction] = useState<PendingAnalyzeAction | null>(null);
  const [pendingMoveAction, setPendingMoveAction] = useState<PendingMoveAction | null>(null);
  const [moveDialogErrorMessage, setMoveDialogErrorMessage] = useState<string | null>(null);
  const [
    selectedDocumentReferenceIdsByMeasurementDateId,
    setSelectedDocumentReferenceIdsByMeasurementDateId,
  ] = useState<Record<number, number[]>>({});
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [
    pendingMeasurementDateSelection,
    setPendingMeasurementDateSelection,
  ] = useState<PendingMeasurementDateSelection | null>(null);
  const [
    pendingMeasurementDateAddFolder,
    setPendingMeasurementDateAddFolder,
  ] = useState<PendingMeasurementDateAddFolderAction | null>(null);
  const [pendingMeasurementDateKey, setPendingMeasurementDateKey] = useState<string | null>(null);
  const [highlightedDocumentReferenceId, setHighlightedDocumentReferenceId] = useState<number | null>(null);
  const [highlightedFolderId, setHighlightedFolderId] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isUploadingDocumentBatch, setIsUploadingDocumentBatch] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<FirmDocumentUploadDialogProgress | null>(null);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [visibleBulkActionDocumentCount, setVisibleBulkActionDocumentCount] = useState(0);
  const [companySearchValue, setCompanySearchValue] = useState('');
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);
  const [preferredMeasurementDateId, setPreferredMeasurementDateId] = useState<number | null>(null);
  const { successNotification } = useResponse();
  const {
    analyzeDocuments,
    copyDocumentReferences,
    createFolder,
    deleteFolder,
    deleteDocumentReferences,
    downloadDocuments,
    editDocumentReference,
    isAnalyzingDocuments,
    isCopyingDocumentReferences,
    isCreatingFolder,
    isDeletingFolder,
    isDeletingDocumentReferences,
    isDownloadingDocuments,
    isEditingDocumentReference,
    isMovingDocumentReferences,
    isRenamingFolder,
    isUploadingDocument,
    moveDocumentReferences,
    renameFolder,
    uploadDocument,
  } = useFirmDocumentActions();
  const {
    setValue: setDocumentSearchQueryValue,
    value: documentSearchQueryValue,
  } = useQueryParamValue({ paramName: FIRM_DOCUMENTS_QUERY_PARAMS.documentSearch });
  const {
    setValue: setMeasurementDateQueryValue,
    value: measurementDateQueryValue,
  } = useQueryParamValue({ paramName: DATE_ALIAS });
  const documentSearchValue = documentSearchQueryValue ?? '';
  const [documentSearchInputValue, setDocumentSearchInputValue] = useState(documentSearchValue);
  const [documentSuggestionSearchValue, setDocumentSuggestionSearchValue] = useState(documentSearchValue);
  const { setValue: setDelayedDocumentSuggestionSearchValue } = useDelayedCommitValue({
    commitDelayMs: DOCUMENT_SEARCH_SUGGESTION_DELAY_MS,
    onCommit: setDocumentSuggestionSearchValue,
    value: documentSuggestionSearchValue,
  });
  const canMutateDocuments = !isRestrictedUser && !isViewOnlyUser;
  // Match legacy Documents: some viewer roles may browse, but Limited/Export viewers cannot download files.
  const canDownloadDocuments = ![LIMITED_VIEWER_VALUE, EXPORT_VIEWER_VALUE].includes(currentFirmRole as number);
  const canUseDocumentSelection = canMutateDocuments || canDownloadDocuments;
  const documentFilterValue = useMemo<FirmDocumentsFilterValue>(() => ({
    fileType: searchParams.get(FIRM_DOCUMENTS_QUERY_PARAMS.documentFileType),
    ordering: getDocumentOrderingQueryValue(searchParams.get(FIRM_DOCUMENTS_QUERY_PARAMS.documentOrdering)),
    referenceObjectType: searchParams.get(FIRM_DOCUMENTS_QUERY_PARAMS.documentReferenceType),
    source: getDocumentSourceQueryValue(searchParams.get(FIRM_DOCUMENTS_QUERY_PARAMS.documentSource)),
    uploadedBy: searchParams.get(FIRM_DOCUMENTS_QUERY_PARAMS.documentUploadedBy),
  }), [searchParams]);
  const activeDocumentFilterCount = getActiveDocumentFilterCount(documentFilterValue);
  const hasActiveDocumentSearchOrFilters = Boolean(documentSearchValue.trim()) || activeDocumentFilterCount > 0;
  // Deep links may target an older measurement date; let the API jump to that page instead of fetching page-by-page.
  const targetMeasurementDateId = getTargetMeasurementDateId(measurementDateQueryValue);
  const targetMeasurementDate = getTargetMeasurementDate(measurementDateQueryValue);
  const hasMeasurementDateTarget = Boolean(targetMeasurementDate || targetMeasurementDateId);
  // Documents owns company-backed tabs because the tertiary nav should stay a controlled primitive.
  // Keep the active company tab on the shell-standard ?tab key; `company` should remain domain/filter state.
  const {
    queryParamValue: selectedCompanySlug,
    setQueryParamValue: setSelectedCompanySlug,
    value: activeDocumentsViewValue,
  } = useTertiaryNavigationQueryParamState({
    defaultValue: ALL_DOCUMENTS_TAB_VALUE,
    getValueFromQueryParam: getCompanyTabValue,
  });
  const documentFilterSignature = [
    documentSearchValue,
    documentFilterValue.fileType,
    documentFilterValue.ordering,
    documentFilterValue.referenceObjectType,
    documentFilterValue.source,
    documentFilterValue.uploadedBy,
  ].join('|');
  const firmDocumentCompanyMetadataQuery = useFirmDocumentCompaniesQuery({ firmId });
  const firmDocumentCompaniesQuery = useFirmDocumentCompaniesQuery({
    firmId,
    fundId: selectedFundId,
    search: companySearchValue,
  });
  const companies = firmDocumentCompaniesQuery.data?.companies ?? EMPTY_COMPANIES;
  const companyMetadata = firmDocumentCompanyMetadataQuery.data?.companies ?? EMPTY_COMPANIES;
  const selectedCompany = useMemo(
    () => openedCompanyTabs.find(company => company.slug === selectedCompanySlug)
      ?? companies.find(company => company.slug === selectedCompanySlug)
      ?? companyMetadata.find(company => company.slug === selectedCompanySlug)
      ?? null,
    [companies, companyMetadata, openedCompanyTabs, selectedCompanySlug]
  );
  const selectedCompanyId = selectedCompany?.id ?? null;
  const isSelectedCompanyResolving = Boolean(selectedCompanySlug)
    && !selectedCompanyId
    && !firmDocumentCompanyMetadataQuery.isError;
  const isCompanyDocumentsView = Boolean(selectedCompanySlug);
  const firmDocumentMeasurementDatesQuery = useFirmDocumentMeasurementDatesQuery({
    companyId: isCompanyDocumentsView ? selectedCompanyId : null,
    fileType: documentFilterValue.fileType,
    firmId,
    referenceObjectType: documentFilterValue.referenceObjectType,
    search: documentSearchValue,
    source: documentFilterValue.source,
    uploadedBy: documentFilterValue.uploadedBy,
    targetDate: targetMeasurementDate,
    targetMeasurementDateId,
  });
  const firmDocumentSuggestionsQuery = useFirmDocumentSuggestionsQuery({
    companyId: isCompanyDocumentsView ? selectedCompanyId : null,
    enabled: !isSelectedCompanyResolving,
    fileType: documentFilterValue.fileType,
    firmId,
    preferredMeasurementDate: targetMeasurementDate,
    referenceObjectType: documentFilterValue.referenceObjectType,
    search: documentSuggestionSearchValue,
    source: documentFilterValue.source,
    uploadedBy: documentFilterValue.uploadedBy,
  });
  const companyMeasurementDatesQuery = useGetMeasurementDatesByCompany({
    companyId: selectedCompanyId ?? undefined,
    shouldQueryAutomatically: Boolean(copyingDocumentReferences.length && selectedCompanyId),
  });
  const {
    fetchNextPage: fetchNextMeasurementDatesPage,
    fetchPreviousPage: fetchPreviousMeasurementDatesPage,
    hasNextPage: hasMoreMeasurementDates,
    hasPreviousPage: hasNewerMeasurementDates,
    isFetchingNextPage: isFetchingNextMeasurementDatesPage,
    isFetchingPreviousPage: isFetchingPreviousMeasurementDatesPage,
    measurementDates,
    remainingNewerCount: remainingNewerMeasurementDateCount,
    remainingCount: remainingMeasurementDateCount,
    targetFound: isTargetMeasurementDateFound,
  } = firmDocumentMeasurementDatesQuery;
  const availableFunds = firmDocumentCompanyMetadataQuery.data?.available_funds
    ?? firmDocumentCompaniesQuery.data?.available_funds
    ?? EMPTY_FUNDS;
  const documentSearchSuggestions = useMemo(
    () => getDocumentSearchSuggestions(
      firmDocumentSuggestionsQuery.data?.suggestions ?? EMPTY_DOCUMENT_SUGGESTIONS,
      isCompanyDocumentsView
    ),
    [firmDocumentSuggestionsQuery.data?.suggestions, isCompanyDocumentsView]
  );
  const normalizedDocumentSearchInputValue = getNormalizedSearch(documentSearchInputValue) ?? '';
  const normalizedDocumentSuggestionSearchValue = getNormalizedSearch(documentSuggestionSearchValue) ?? '';
  const isDocumentSuggestionInputReady = normalizedDocumentSearchInputValue.length
    >= FIRM_DOCUMENT_SUGGESTIONS_QUERY_MIN_SEARCH_LENGTH;
  const isDocumentSuggestionSearchSettled =
    normalizedDocumentSearchInputValue === normalizedDocumentSuggestionSearchValue;
  const isDocumentSuggestionQueryLoading = isDocumentSuggestionSearchSettled
    && firmDocumentSuggestionsQuery.fetchStatus === 'fetching'
    && !firmDocumentSuggestionsQuery.data;
  const isDocumentSuggestionsLoading = isDocumentSuggestionInputReady && (
    !isDocumentSuggestionSearchSettled
      || isDocumentSuggestionQueryLoading
  );
  const activeMeasurementDates = useMemo<FirmDocumentMeasurementDate[]>(
    () => getSortedMeasurementDates(measurementDates ?? EMPTY_MEASUREMENT_DATES),
    [measurementDates]
  );
  const copyTargetMeasurementDates = useMemo<MeasurementDateName[]>(
    () => getSortedMeasurementDates(
      (companyMeasurementDatesQuery.data ?? activeMeasurementDates) as MeasurementDateName[]
    ),
    [activeMeasurementDates, companyMeasurementDatesQuery.data]
  );
  const selectedMeasurementDate = useMemo(
    () => getSelectedMeasurementDate(
      activeMeasurementDates,
      measurementDateQueryValue,
      preferredMeasurementDateId
    ),
    [
      activeMeasurementDates,
      measurementDateQueryValue,
      preferredMeasurementDateId,
    ]
  );
  const selectedMeasurementDateId = selectedMeasurementDate?.id ?? null;
  const firmDocumentFoldersQuery = useFirmDocumentFoldersQuery({
    companyId: isCompanyDocumentsView ? selectedCompanyId : null,
    enabled: !isSelectedCompanyResolving,
    firmId,
    measurementDateId: selectedMeasurementDateId,
  });
  const firmDocumentFilterOptionsQuery = useFirmDocumentFilterOptionsQuery({
    companyId: isCompanyDocumentsView ? selectedCompanyId : null,
    enabled: !isSelectedCompanyResolving,
    firmId,
    measurementDate: isCompanyDocumentsView ? null : selectedMeasurementDate?.date,
    measurementDateId: isCompanyDocumentsView ? selectedMeasurementDateId : null,
    search: documentSearchValue,
  });
  const activeDocumentFilterLabels = useMemo(
    () => getActiveDocumentFilterLabels(documentFilterValue, firmDocumentFilterOptionsQuery.data),
    [documentFilterValue, firmDocumentFilterOptionsQuery.data]
  );
  const documentFilterTooltipTitle = useMemo(() => {
    if (!activeDocumentFilterLabels.length) {
      return FIRM_DOCUMENTS_LABELS.sortAndFilter;
    }

    return (
      <FirmDocumentsFilterTooltip>
        <span data-firm-documents-filter-tooltip-title="true">
          {FIRM_DOCUMENTS_LABELS.sortAndFilter}
        </span>
        {activeDocumentFilterLabels.map(label => (
          <span key={label}>
            {label}
          </span>
        ))}
      </FirmDocumentsFilterTooltip>
    );
  }, [activeDocumentFilterLabels]);
  const firmDocumentsQuery = useFirmDocumentsQuery({
    companyId: isCompanyDocumentsView ? selectedCompanyId : null,
    enabled: !isSelectedCompanyResolving && Boolean(
      isCompanyDocumentsView
        ? selectedCompanyId && selectedMeasurementDate?.id
        : selectedMeasurementDate?.date
    ),
    fileType: documentFilterValue.fileType,
    firmId,
    measurementDate: isCompanyDocumentsView ? null : selectedMeasurementDate?.date,
    measurementDateId: isCompanyDocumentsView ? selectedMeasurementDate?.id : null,
    ordering: documentFilterValue.ordering,
    referenceObjectType: documentFilterValue.referenceObjectType,
    search: documentSearchValue,
    source: documentFilterValue.source,
    uploadedBy: documentFilterValue.uploadedBy,
  });
  const firmDocumentPages = firmDocumentsQuery.data?.pages;
  const documents = useMemo(
    () => firmDocumentPages?.flatMap(page => page.results) ?? EMPTY_DOCUMENTS,
    [firmDocumentPages]
  );
  const totalDocumentCount = firmDocumentPages?.[0]?.count ?? documents.length;
  const remainingDocumentCount = Math.max(totalDocumentCount - documents.length, 0);
  const fetchNextDocumentsPage = firmDocumentsQuery.fetchNextPage;
  const hasMoreDocuments = Boolean(firmDocumentsQuery.hasNextPage);
  const isFetchingNextDocumentsPage = firmDocumentsQuery.isFetchingNextPage;

  // In company tab view, auto-fetch remaining pages so all documents are visible
  // without a manual "Show more" click, matching the all-at-once load on the
  // legacy company Documents page.
  useEffect(() => {
    if (!isCompanyDocumentsView || !hasMoreDocuments || isFetchingNextDocumentsPage) {
      return;
    }

    fetchNextDocumentsPage();
  }, [fetchNextDocumentsPage, hasMoreDocuments, isCompanyDocumentsView, isFetchingNextDocumentsPage]);

  const firmLevelMeasurementDateDocuments = useMemo(
    () => getFirmLevelMeasurementDateDocuments(
      selectedMeasurementDate,
      documents,
      firmDocumentFoldersQuery.data?.folders
    ),
    [documents, firmDocumentFoldersQuery.data?.folders, selectedMeasurementDate]
  );
  const companyLevelMeasurementDateDocuments = useMemo(
    () => getCompanyLevelMeasurementDateDocuments(
      selectedMeasurementDate,
      documents,
      firmDocumentFoldersQuery.data?.folders
    ),
    [documents, firmDocumentFoldersQuery.data?.folders, selectedMeasurementDate]
  );
  const selectedMeasurementDateDocumentsIdFromLoadedData = useMemo(
    () => getMeasurementDateDocumentsId(companyLevelMeasurementDateDocuments),
    [companyLevelMeasurementDateDocuments]
  );
  const selectedMeasurementDateDocumentsQuery = useFirmDocumentMeasurementDateDocumentsQuery({
    companyId: selectedCompanyId,
    enabled: Boolean(
      isCompanyDocumentsView
        && !isSelectedCompanyResolving
        && !selectedMeasurementDateDocumentsIdFromLoadedData
        && firmDocumentsQuery.dataUpdatedAt > 0
        && firmDocumentFoldersQuery.dataUpdatedAt > 0
    ),
    measurementDateId: selectedMeasurementDateId,
  });
  const selectedMeasurementDateDocumentsId = useMemo(
    () => selectedMeasurementDateDocumentsIdFromLoadedData
      ?? selectedMeasurementDateDocumentsQuery.data?.id
      ?? null,
    [selectedMeasurementDateDocumentsIdFromLoadedData, selectedMeasurementDateDocumentsQuery.data?.id]
  );
  const editingDocumentCompanyId = editingDocumentReference
    ? getDocumentReferenceCompanyId(editingDocumentReference) ?? selectedCompanyId
    : null;
  const editingDocumentMeasurementDateId = editingDocumentReference
    ? getDocumentReferenceMeasurementDateId(editingDocumentReference, selectedMeasurementDateId)
    : null;
  const firmDocumentRequestedFilesQuery = useFirmDocumentRequestedFilesQuery({
    companyId: editingDocumentCompanyId,
    enabled: Boolean(editingDocumentReference),
    firmId,
    measurementDateId: editingDocumentMeasurementDateId,
  });
  const selectedMeasurementDateCachedDocuments = selectedMeasurementDateId
    ? measurementDateDocumentsByIdRef.current[selectedMeasurementDateId] ?? null
    : null;
  const activeMeasurementDateDocumentReferenceIds = useMemo(
    () => documents.map(documentReference => documentReference.document_reference_id),
    [documents]
  );
  const selectedDocumentReferenceIds = useMemo(
    () => new Set(
      selectedMeasurementDateId
        ? selectedDocumentReferenceIdsByMeasurementDateId[selectedMeasurementDateId] ?? EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS
        : EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS
    ),
    [selectedDocumentReferenceIdsByMeasurementDateId, selectedMeasurementDateId]
  );
  const activeDocumentReferences = documents;
  const isHighlightedDocumentVisible = useMemo(
    () => Boolean(highlightedDocumentReferenceId) && activeDocumentReferences.some(
      documentReference => documentReference.document_reference_id === highlightedDocumentReferenceId
    ),
    [activeDocumentReferences, highlightedDocumentReferenceId]
  );
  const selectedDocumentReferences = useMemo(
    () => activeDocumentReferences.filter(documentReference =>
      selectedDocumentReferenceIds.has(documentReference.document_reference_id)
    ),
    [activeDocumentReferences, selectedDocumentReferenceIds]
  );
  const selectedDocumentCount = selectedDocumentReferences.length;
  const isBulkActionsVisible = selectedDocumentCount > 0;
  const shouldRenderBulkActions = isBulkActionsVisible || visibleBulkActionDocumentCount > 0;
  const bulkActionDocumentCount = isBulkActionsVisible ? selectedDocumentCount : visibleBulkActionDocumentCount;
  const shouldShowBulkDeleteDivider = canMutateDocuments && (canDownloadDocuments || isCompanyDocumentsView);

  // Keep Bulk Actions mounted briefly after selection clears so the tertiary action can animate out.
  useEffect(() => {
    if (selectedDocumentCount > 0) {
      setVisibleBulkActionDocumentCount(currentCount => (
        currentCount === selectedDocumentCount ? currentCount : selectedDocumentCount
      ));
      return undefined;
    }

    if (visibleBulkActionDocumentCount === 0) {
      return undefined;
    }

    const timeoutId = window.setTimeout(
      () => setVisibleBulkActionDocumentCount(0),
      TERTIARY_NAVIGATION_ACTION_UNMOUNT_DELAY_MS
    );

    return () => window.clearTimeout(timeoutId);
  }, [selectedDocumentCount, visibleBulkActionDocumentCount]);

  // Clear the menu anchor when selection disappears so it cannot reopen from a stale hidden button.
  useEffect(() => {
    if (selectedDocumentCount === 0) {
      setBulkActionsAnchorEl(null);
    }
  }, [selectedDocumentCount]);

  const selectableDocumentCount = activeMeasurementDateDocumentReferenceIds.length;
  const isAnalyzeDialogOpen = Boolean(pendingAnalyzeAction);
  const isCopyDialogOpen = copyingDocumentReferences.length > 0;
  const isMoveDialogOpen = Boolean(pendingMoveAction);
  const isDownloadDialogOpen = downloadingDocumentReferences.length > 0;
  const isDeleteDialogOpen = deletingDocumentReferences.length > 0;
  const isDocumentEditDialogOpen = Boolean(editingDocumentReference);
  const isFolderDeleteDialogOpen = Boolean(deletingFolder);
  const isFolderEditDialogOpen = Boolean(editingFolder);
  const isFolderCreateDialogOpen = Boolean(creatingFolder);
  const isUploadDialogOpen = Boolean(uploadingDocumentContext);
  const isUploadDialogBusy = isUploadingDocument || isUploadingDocumentBatch;
  const visiblePendingAnalyzeAction = useRetainedDialogValue(pendingAnalyzeAction, isAnalyzeDialogOpen);
  const visibleCopyingDocumentReferences = useRetainedDialogValue(copyingDocumentReferences, isCopyDialogOpen);
  const visiblePendingMoveAction = useRetainedDialogValue(pendingMoveAction, isMoveDialogOpen);
  const visibleDownloadingDocumentReferences = useRetainedDialogValue(
    downloadingDocumentReferences,
    isDownloadDialogOpen
  );
  const visibleDeletingDocumentReferences = useRetainedDialogValue(deletingDocumentReferences, isDeleteDialogOpen);
  const visibleEditingDocumentReference = useRetainedDialogValue(
    editingDocumentReference,
    isDocumentEditDialogOpen
  );
  const visibleDeletingFolder = useRetainedDialogValue(deletingFolder, isFolderDeleteDialogOpen);
  const visibleEditingFolder = useRetainedDialogValue(editingFolder, isFolderEditDialogOpen);
  const visibleCreatingFolder = useRetainedDialogValue(creatingFolder, isFolderCreateDialogOpen);
  const visibleUploadingDocumentContext = useRetainedDialogValue(uploadingDocumentContext, isUploadDialogOpen);

  const clearUploadDialogState = useCallback(() => {
    setUploadingDocumentContext(null);
    setUploadProgress(null);
    setIsUploadComplete(false);
  }, []);
  const uploadAutoCloseSeconds = useCountdown({
    enabled: isUploadDialogOpen && isUploadComplete,
    initialCountdown: UPLOAD_AUTO_CLOSE_SECONDS,
    onComplete: clearUploadDialogState,
  });
  const visibleRequestedFileOptions = useRetainedDialogValue(
    firmDocumentRequestedFilesQuery.data?.requested_files ?? [],
    isDocumentEditDialogOpen
  );
  const selectedActiveDocumentCount = useMemo(
    () => activeMeasurementDateDocumentReferenceIds.filter(documentReferenceId =>
      selectedDocumentReferenceIds.has(documentReferenceId)
    ).length,
    [activeMeasurementDateDocumentReferenceIds, selectedDocumentReferenceIds]
  );
  const isSelectAllDocumentsChecked = selectableDocumentCount > 0
    && selectedActiveDocumentCount === selectableDocumentCount;
  const isSelectAllDocumentsIndeterminate = selectedActiveDocumentCount > 0
    && selectedActiveDocumentCount < selectableDocumentCount;
  const deletingDocumentFileNames = useMemo(
    () => visibleDeletingDocumentReferences.map(getFirmDocumentReferenceFileName),
    [visibleDeletingDocumentReferences]
  );
  const downloadingDocumentFileNames = useMemo(
    () => visibleDownloadingDocumentReferences.map(getFirmDocumentReferenceFileName),
    [visibleDownloadingDocumentReferences]
  );
  const pendingAnalyzeDocumentFileNames = useMemo(
    () => visiblePendingAnalyzeAction?.documentReferences.map(getFirmDocumentReferenceFileName) ?? [],
    [visiblePendingAnalyzeAction]
  );
  const pendingMoveDocumentFileName = visiblePendingMoveAction
    ? getFirmDocumentReferenceFileName(visiblePendingMoveAction.documentReference)
    : undefined;
  const pendingAnalyzeActionLabel = visiblePendingAnalyzeAction
    ? getAnalyzeActionLabel(visiblePendingAnalyzeAction.processingType)
    : FIRM_DOCUMENTS_LABELS.analyzeCapTable;
  const isDocumentActionInProgress = isCopyingDocumentReferences
    || isAnalyzingDocuments
    || isCreatingFolder
    || isDeletingFolder
    || isDeletingDocumentReferences
    || isDownloadingDocuments
    || isEditingDocumentReference
    || isMovingDocumentReferences
    || isRenamingFolder
    || isUploadingDocument;
  const isCompanyListLoading = !firmId
    || firmDocumentCompaniesQuery.isLoading
    || firmDocumentCompaniesQuery.isFetching;
  const isMeasurementDatesLoading = !firmId
    || (isSelectedCompanyResolving
      || firmDocumentMeasurementDatesQuery.isLoading
      || (firmDocumentMeasurementDatesQuery.isFetching && !firmDocumentMeasurementDatesQuery.data));
  const isCopyTargetMeasurementDatesLoading = Boolean(copyingDocumentReferences.length)
    && (companyMeasurementDatesQuery.isLoading
      || (companyMeasurementDatesQuery.isFetching && !companyMeasurementDatesQuery.data));
  const isFolderListLoading = Boolean(selectedMeasurementDateId)
    && (
      firmDocumentFoldersQuery.isPending
      || (firmDocumentFoldersQuery.isFetching && !firmDocumentFoldersQuery.data)
    );
  const isMeasurementDateDocumentsLoading = Boolean(selectedMeasurementDate?.id)
    && (
      firmDocumentsQuery.isPending
      || (firmDocumentsQuery.isFetching && !firmDocumentsQuery.data)
      || isFolderListLoading
    );
  const isSelectAllDocumentsDisabled = !selectedMeasurementDateId
    || !canUseDocumentSelection
    || selectableDocumentCount === 0
    || isMeasurementDateDocumentsLoading
    || isDocumentActionInProgress;
  const selectedMeasurementDateDocuments = isCompanyDocumentsView
    ? companyLevelMeasurementDateDocuments
    : firmLevelMeasurementDateDocuments;
  const hasSelectedMeasurementDateDocumentsResponse =
    firmDocumentsQuery.dataUpdatedAt > 0 || firmDocumentsQuery.isError;
  const shouldOpenSelectedMeasurementDate = Boolean(selectedMeasurementDate)
    && (
      !isMeasurementDateDocumentsLoading
      || Boolean(selectedMeasurementDateDocuments)
      || Boolean(selectedMeasurementDateCachedDocuments)
    );
  const selectedMeasurementDateKey = selectedMeasurementDate ? getMeasurementDateKey(selectedMeasurementDate) : null;
  const isSelectedMeasurementDatePending = pendingMeasurementDateKey === selectedMeasurementDateKey;
  const activeMeasurementDateKey = shouldOpenSelectedMeasurementDate && !isSelectedMeasurementDatePending
    ? selectedMeasurementDateKey
    : null;
  const pendingAccordionMeasurementDateKey = selectedMeasurementDateKey && isSelectedMeasurementDatePending
    ? selectedMeasurementDateKey
    : null;

  const handleFundFilterChange = useCallback((value: EntityListPanelFilterValue) => {
    setSelectedFundId(getFundIdFromFilterValue(value));
  }, []);

  // Reset the fund filter if the refreshed company metadata no longer exposes the selected fund.
  useEffect(() => {
    if (selectedFundId && !availableFunds.some(fund => fund.id === selectedFundId)) {
      setSelectedFundId(null);
    }
  }, [availableFunds, selectedFundId]);

  const handleCompanySearchChange = useCallback((value: string) => {
    startTransition(() => {
      setCompanySearchValue(value);
    });
  }, []);

  const handleDocumentSearchChange = useCallback((value: string) => {
    startTransition(() => {
      setDocumentSearchQueryValue(value || null, { replace: true });
    });
  }, [setDocumentSearchQueryValue]);

  const handleDocumentSearchInputValueChange = useCallback((value: string) => {
    setDocumentSearchInputValue(value);
    setDelayedDocumentSuggestionSearchValue(value);
  }, [setDelayedDocumentSuggestionSearchValue]);

  const openDocumentSearchSuggestionContext = useCallback((
    suggestion: FirmDocumentSuggestion,
    { shouldHighlightDocument = false }: { shouldHighlightDocument?: boolean } = {}
  ) => {
    if (shouldHighlightDocument) {
      setHighlightedDocumentReferenceId(suggestion.document_reference_id);
    }

    setPreferredMeasurementDateId(suggestion.measurement_date.id);
    setMeasurementDateQueryValue(suggestion.measurement_date.date, { replace: true });
  }, [setMeasurementDateQueryValue]);

  const handleDocumentSearchSubmit = useCallback((value: string) => {
    setDocumentSearchInputValue(value);
    setDocumentSuggestionSearchValue(value);
    handleDocumentSearchChange(value);
  }, [handleDocumentSearchChange]);

  const handleDocumentSearchSuggestionSelect = useCallback((suggestion: TertiaryNavigationSearchSuggestion) => {
    const searchValue = suggestion.inputValue ?? '';
    const selectedSuggestion = firmDocumentSuggestionsQuery.data?.suggestions.find(
      documentSuggestion => String(documentSuggestion.document_reference_id) === suggestion.value
    );

    setDocumentSearchInputValue(searchValue);
    setDocumentSuggestionSearchValue(searchValue);
    handleDocumentSearchChange(searchValue);

    if (!selectedSuggestion) {
      return;
    }

    openDocumentSearchSuggestionContext(selectedSuggestion, { shouldHighlightDocument: true });
  }, [
    firmDocumentSuggestionsQuery.data?.suggestions,
    handleDocumentSearchChange,
    openDocumentSearchSuggestionContext,
  ]);

  const updateDocumentFilterQueryParams = useCallback((nextValue: FirmDocumentsFilterValue) => {
    startTransition(() => {
      setSearchParams(prevSearchParams => {
        const nextSearchParams = new URLSearchParams(prevSearchParams);

        setQueryParam(nextSearchParams, FIRM_DOCUMENTS_QUERY_PARAMS.documentFileType, nextValue.fileType);
        setQueryParam(nextSearchParams, FIRM_DOCUMENTS_QUERY_PARAMS.documentOrdering, nextValue.ordering);
        setQueryParam(
          nextSearchParams,
          FIRM_DOCUMENTS_QUERY_PARAMS.documentReferenceType,
          nextValue.referenceObjectType
        );
        setQueryParam(nextSearchParams, FIRM_DOCUMENTS_QUERY_PARAMS.documentSource, nextValue.source);
        setQueryParam(nextSearchParams, FIRM_DOCUMENTS_QUERY_PARAMS.documentUploadedBy, nextValue.uploadedBy);

        if (nextSearchParams.toString() === prevSearchParams.toString()) {
          return prevSearchParams;
        }

        return nextSearchParams;
      }, { replace: true });
    });
  }, [setSearchParams]);

  const handleDocumentFilterApply = useCallback((nextValue: FirmDocumentsFilterValue) => {
    updateDocumentFilterQueryParams(nextValue);
    setIsFilterDialogOpen(false);
  }, [updateDocumentFilterQueryParams]);

  const handleDocumentFilterOpen = useCallback(() => {
    setIsFilterDialogOpen(true);
  }, []);

  const handleFullscreenToggle = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => undefined);
      return;
    }

    document.documentElement.requestFullscreen?.().catch(() => undefined);
  }, []);
  const fullscreenActionLabel = isFullscreen
    ? FIRM_DOCUMENTS_LABELS.exitFullScreen
    : FIRM_DOCUMENTS_LABELS.fullScreen;

  const navigateToDocumentsView = useCallback((companySlug: string | null, { replace = false } = {}) => {
    const currentScope = getDocumentsViewQueryScope(selectedCompanySlug);
    const targetScope = getDocumentsViewQueryScope(companySlug);
    const scopedQueryByScope = documentsViewScopedQueryByScopeRef.current;

    setSearchParams(prevSearchParams => {
      const currentScopedQuery = getDocumentsViewScopedQuery(prevSearchParams);
      const targetScopedQuery = currentScope === targetScope
        ? currentScopedQuery
        : scopedQueryByScope.get(targetScope);

      scopedQueryByScope.set(currentScope, currentScopedQuery);

      const nextSearchParams = applyDocumentsViewScopedQuery({
        fallbackDate: prevSearchParams.get(DATE_ALIAS),
        searchParams: prevSearchParams,
        scopedQuery: targetScopedQuery,
      });

      setQueryParam(nextSearchParams, TERTIARY_NAVIGATION_QUERY_PARAMS.tab, companySlug);

      if (nextSearchParams.toString() === prevSearchParams.toString()) {
        return prevSearchParams;
      }

      return nextSearchParams;
    }, { replace });
  }, [selectedCompanySlug, setSearchParams]);

  const handleCompanySelect = useCallback((company: FirmDocumentCompany) => {
    setOpenedCompanyTabs(currentTabs => {
      if (currentTabs.some(tab => tab.id === company.id)) {
        return currentTabs;
      }

      return [...currentTabs, company];
    });
    navigateToDocumentsView(company.slug);
  }, [navigateToDocumentsView]);

  const handleCompanyTabClose = useCallback((companyId: number) => {
    const closingCompany = openedCompanyTabs.find(company => company.id === companyId);

    setOpenedCompanyTabs(currentTabs => currentTabs.filter(tab => tab.id !== companyId));
    if (closingCompany?.slug === selectedCompanySlug) {
      navigateToDocumentsView(null);
    }
  }, [navigateToDocumentsView, openedCompanyTabs, selectedCompanySlug]);

  const handleMeasurementDateSelect = useCallback((measurementDate: FirmDocumentMeasurementDate) => {
    const measurementDateKey = getMeasurementDateKey(measurementDate);
    const hasCachedMeasurementDateDocuments = Boolean(
      measurementDate.id && measurementDateDocumentsByIdRef.current[measurementDate.id]
    );

    setPreferredMeasurementDateId(measurementDate.id);
    setPendingMeasurementDateKey(hasCachedMeasurementDateDocuments ? null : measurementDateKey);
    setMeasurementDateQueryValue(measurementDate.date, { replace: true });
  }, [setMeasurementDateQueryValue]);

  const handleShowMoreMeasurementDates = useCallback(() => {
    if (!hasMoreMeasurementDates || isFetchingNextMeasurementDatesPage) {
      return;
    }

    fetchNextMeasurementDatesPage();
  }, [
    fetchNextMeasurementDatesPage,
    hasMoreMeasurementDates,
    isFetchingNextMeasurementDatesPage,
  ]);

  const handleShowNewerMeasurementDates = useCallback(() => {
    if (!hasNewerMeasurementDates || isFetchingPreviousMeasurementDatesPage) {
      return;
    }

    fetchPreviousMeasurementDatesPage();
  }, [
    fetchPreviousMeasurementDatesPage,
    hasNewerMeasurementDates,
    isFetchingPreviousMeasurementDatesPage,
  ]);

  const handleShowMoreDocuments = useCallback(() => {
    if (!hasMoreDocuments || isFetchingNextDocumentsPage) {
      return;
    }

    fetchNextDocumentsPage();
  }, [
    fetchNextDocumentsPage,
    hasMoreDocuments,
    isFetchingNextDocumentsPage,
  ]);

  const handleDocumentSelectionChange = useCallback((documentReferenceId: number, checked: boolean) => {
    if (!canUseDocumentSelection || !selectedMeasurementDateId) {
      return;
    }

    setSelectedDocumentReferenceIdsByMeasurementDateId(currentSelection => {
      const selectedIds = new Set(currentSelection[selectedMeasurementDateId] ?? EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS);

      if (checked) {
        selectedIds.add(documentReferenceId);
      } else {
        selectedIds.delete(documentReferenceId);
      }

      return {
        ...currentSelection,
        [selectedMeasurementDateId]: [...selectedIds],
      };
    });
  }, [canUseDocumentSelection, selectedMeasurementDateId]);

  const handleDocumentReferencesSelectionChange = useCallback((documentReferenceIds: number[], checked: boolean) => {
    if (!canUseDocumentSelection || !selectedMeasurementDateId || !documentReferenceIds.length) {
      return;
    }

    setSelectedDocumentReferenceIdsByMeasurementDateId(currentSelection => {
      const selectedIds = new Set(currentSelection[selectedMeasurementDateId] ?? EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS);

      documentReferenceIds.forEach(documentReferenceId => {
        if (checked) {
          selectedIds.add(documentReferenceId);
          return;
        }

        selectedIds.delete(documentReferenceId);
      });

      return {
        ...currentSelection,
        [selectedMeasurementDateId]: [...selectedIds],
      };
    });
  }, [canUseDocumentSelection, selectedMeasurementDateId]);

  const handleSelectAllDocumentsChange = useCallback((checked: boolean) => {
    if (!canUseDocumentSelection || !selectedMeasurementDateId) {
      return;
    }

    setSelectedDocumentReferenceIdsByMeasurementDateId(currentSelection => ({
      ...currentSelection,
      [selectedMeasurementDateId]: checked
        ? activeMeasurementDateDocumentReferenceIds
        : EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS,
    }));
  }, [activeMeasurementDateDocumentReferenceIds, canUseDocumentSelection, selectedMeasurementDateId]);

  const clearSelectedDocumentReferences = useCallback((documentReferenceIds: Iterable<number>) => {
    const documentReferenceIdsToClear = new Set(documentReferenceIds);

    if (!documentReferenceIdsToClear.size) {
      return;
    }

    setSelectedDocumentReferenceIdsByMeasurementDateId(currentSelection => {
      let hasSelectionChange = false;
      const nextSelection: Record<number, number[]> = {};

      Object.entries(currentSelection).forEach(([measurementDateId, selectedIds]) => {
        const nextSelectedIds = selectedIds.filter(documentReferenceId =>
          !documentReferenceIdsToClear.has(documentReferenceId)
        );

        if (nextSelectedIds.length !== selectedIds.length) {
          hasSelectionChange = true;
        }

        if (nextSelectedIds.length > 0) {
          nextSelection[Number(measurementDateId)] = nextSelectedIds;
        }
      });

      return hasSelectionChange ? nextSelection : currentSelection;
    });
  }, []);

  const handleDocumentDownload = useCallback((documentReference: FirmDocumentFileActionReference) => {
    if (!canDownloadDocuments) {
      return;
    }

    const downloadTarget = getFirmDocumentDownloadTarget(documentReference, firmId);

    if (!downloadTarget) {
      return;
    }

    const documentReferenceId = documentReference.document_reference_id;
    setDownloadingDocumentReferenceId(documentReferenceId);
    downloadDocuments({ documents: [downloadTarget] })
      .catch(() => undefined)
      .finally(() => {
        setDownloadingDocumentReferenceId(currentDocumentReferenceId => (
          currentDocumentReferenceId === documentReferenceId ? null : currentDocumentReferenceId
        ));
      });
  }, [canDownloadDocuments, downloadDocuments, firmId]);

  const handleDocumentCopy = useCallback((documentReference: FirmDocumentFileActionReference) => {
    if (!canMutateDocuments) {
      return;
    }

    setInitialCopyTargetMeasurementDateId(null);
    setCopyDialogErrorMessage(null);
    setCopyingDocumentReferences([documentReference]);
  }, [canMutateDocuments]);

  const handleDocumentCopyToMeasurementDate = useCallback((
    documentReference: FirmDocumentFileActionReference,
    {
      targetMeasurementDateId: targetCopyMeasurementDateId,
    }: {
      targetMeasurementDateId: number;
      targetMeasurementDateLabel: string;
    }
  ) => {
    if (!canMutateDocuments) {
      return;
    }

    setInitialCopyTargetMeasurementDateId(targetCopyMeasurementDateId);
    setCopyDialogErrorMessage(null);
    setCopyingDocumentReferences([documentReference]);
  }, [canMutateDocuments]);

  const handleDocumentRename = useCallback((documentReference: FirmDocumentFileActionReference) => {
    if (!canMutateDocuments) {
      return;
    }

    setEditingDocumentReference(documentReference);
  }, [canMutateDocuments]);

  const handleFolderRename = useCallback((folder: FirmDocumentTreeFolder) => {
    if (!canMutateDocuments) {
      return;
    }

    const folderActionTarget = getFolderActionTarget(folder);

    if (!folderActionTarget) {
      return;
    }

    setEditingFolder({
      currentParentFolderId: getFolderParentFolderId(folder),
      folderId: folderActionTarget.folderId,
      folderName: folderActionTarget.folderName,
      mdDocumentsId: folderActionTarget.mdDocumentsId,
      parentFolderOptions: getFolderParentOptions(selectedMeasurementDateDocuments, folder),
      siblingFolderNamesByTargetKey: getFolderCreateSiblingFolderNamesByTargetKey(
        selectedMeasurementDateDocuments,
        [folderActionTarget.mdDocumentsId],
        folderActionTarget.folderId
      ),
    });
  }, [canMutateDocuments, selectedMeasurementDateDocuments]);

  const handleFolderDelete = useCallback((folder: FirmDocumentTreeFolder) => {
    if (!canMutateDocuments) {
      return;
    }

    const folderActionTarget = getFolderActionTarget(folder);

    if (folderActionTarget) {
      setDeletingFolder(folderActionTarget);
    }
  }, [canMutateDocuments]);

  const openMeasurementDateAddFolderDialog = useCallback((measurementDate: FirmDocumentMeasurementDate) => {
    if (!isCompanyDocumentsView) {
      const companyOptions = getFolderCreateCompanyOptions(
        selectedMeasurementDateDocuments,
        documentFilterValue.ordering
      );
      const mdDocumentsIds = companyOptions.map(companyOption => companyOption.mdDocumentsId);
      const parentFolderOptionsByCompanyId = companyOptions.reduce<Record<number, FirmDocumentFolderParentOption[]>>(
        (accumulator, companyOption) => {
          accumulator[companyOption.id] = getFolderCreateParentOptions(
            selectedMeasurementDateDocuments,
            companyOption.mdDocumentsId
          );

          return accumulator;
        },
        {}
      );

      if (!companyOptions.length) {
        return false;
      }

      setCreatingFolder({
        companyOptions,
        initialCompanyId: companyOptions.length === 1 ? companyOptions[0].id : null,
        initialParentFolderId: null,
        measurementDateLabel: getMeasurementDateDisplayLabel(measurementDate),
        parentFolderOptionsByCompanyId,
        siblingFolderNames: [],
        siblingFolderNamesByTargetKey: getFolderCreateSiblingFolderNamesByTargetKey(
          selectedMeasurementDateDocuments,
          mdDocumentsIds
        ),
      });
      return true;
    }

    if (!selectedMeasurementDateDocumentsId) {
      return false;
    }

    setCreatingFolder({
      initialParentFolderId: null,
      measurementDateLabel: getMeasurementDateDisplayLabel(measurementDate),
      mdDocumentsId: selectedMeasurementDateDocumentsId,
      parentFolderId: null,
      parentFolderName: null,
      parentFolderOptions: getFolderCreateParentOptions(
        companyLevelMeasurementDateDocuments,
        selectedMeasurementDateDocumentsId
      ),
      siblingFolderNames: getRootFolderNames(companyLevelMeasurementDateDocuments),
      siblingFolderNamesByTargetKey: getFolderCreateSiblingFolderNamesByTargetKey(
        companyLevelMeasurementDateDocuments,
        [selectedMeasurementDateDocumentsId]
      ),
      targetCompanyName: selectedCompany?.name ?? null,
    });
    return true;
  }, [
    companyLevelMeasurementDateDocuments,
    documentFilterValue.ordering,
    isCompanyDocumentsView,
    selectedCompany,
    selectedMeasurementDateDocuments,
    selectedMeasurementDateDocumentsId,
  ]);

  const handleMeasurementDateAddFolder = useCallback((measurementDate: FirmDocumentMeasurementDate) => {
    if (!canMutateDocuments) {
      return;
    }

    const measurementDateKey = getMeasurementDateKey(measurementDate);
    const shouldSwitchMeasurementDate = measurementDate.id !== selectedMeasurementDateId
      || measurementDateKey !== selectedMeasurementDateKey;

    if (shouldSwitchMeasurementDate) {
      setPendingMeasurementDateAddFolder({ measurementDate, measurementDateKey });
      handleMeasurementDateSelect(measurementDate);
      return;
    }

    if (!openMeasurementDateAddFolderDialog(measurementDate)) {
      setPendingMeasurementDateAddFolder({ measurementDate, measurementDateKey });
    }
  }, [
    canMutateDocuments,
    handleMeasurementDateSelect,
    openMeasurementDateAddFolderDialog,
    selectedMeasurementDateId,
    selectedMeasurementDateKey,
  ]);

  const handleMeasurementDateUploadDocument = useCallback((measurementDate: FirmDocumentMeasurementDate) => {
    if (!canMutateDocuments) {
      return;
    }

    handleMeasurementDateSelect(measurementDate);
    setUploadingDocumentContext({
      initialCompanyId: isCompanyDocumentsView ? selectedCompanyId : null,
      initialFolderId: null,
      initialMeasurementDateDate: measurementDate.date,
      initialMeasurementDateId: isCompanyDocumentsView ? measurementDate.id : null,
      isCompanyLocked: isCompanyDocumentsView,
    });
  }, [canMutateDocuments, handleMeasurementDateSelect, isCompanyDocumentsView, selectedCompanyId]);

  const handleFolderAddSubfolder = useCallback((folder: FirmDocumentTreeFolder) => {
    if (!canMutateDocuments) {
      return;
    }

    if (folder.isCompanyGroup) {
      if (!folder.md_documents) {
        return;
      }

      setCreatingFolder({
        initialParentFolderId: null,
        measurementDateLabel: selectedMeasurementDate ? getMeasurementDateDisplayLabel(selectedMeasurementDate) : null,
        mdDocumentsId: folder.md_documents,
        parentFolderId: null,
        parentFolderName: folder.name,
        parentFolderOptions: getFolderCreateParentOptions(selectedMeasurementDateDocuments, folder.md_documents),
        siblingFolderNames: getSubFolderNames(folder),
        siblingFolderNamesByTargetKey: getFolderCreateSiblingFolderNamesByTargetKey(
          selectedMeasurementDateDocuments,
          [folder.md_documents]
        ),
        targetCompanyName: folder.name,
      });
      return;
    }

    const folderActionTarget = getFolderActionTarget(folder);

    if (!folderActionTarget) {
      return;
    }

    setCreatingFolder({
      initialParentFolderId: folderActionTarget.folderId,
      measurementDateLabel: selectedMeasurementDate ? getMeasurementDateDisplayLabel(selectedMeasurementDate) : null,
      mdDocumentsId: folderActionTarget.mdDocumentsId,
      parentFolderId: folderActionTarget.folderId,
      parentFolderName: folderActionTarget.folderName,
      parentFolderOptions: getFolderCreateParentOptions(selectedMeasurementDateDocuments, folderActionTarget.mdDocumentsId),
      siblingFolderNames: getSubFolderNames(folder),
      siblingFolderNamesByTargetKey: getFolderCreateSiblingFolderNamesByTargetKey(
        selectedMeasurementDateDocuments,
        [folderActionTarget.mdDocumentsId]
      ),
      targetCompanyName: isCompanyDocumentsView
        ? selectedCompany?.name ?? null
        : getFolderCompanyName(folder, selectedMeasurementDateDocuments),
    });
  }, [
    canMutateDocuments,
    isCompanyDocumentsView,
    selectedCompany,
    selectedMeasurementDate,
    selectedMeasurementDateDocuments,
  ]);

  const handleFolderUploadDocument = useCallback((folder: FirmDocumentTreeFolder) => {
    if (!canMutateDocuments || !folder.md_documents) {
      return;
    }

    const initialCompanyId = isCompanyDocumentsView
      ? selectedCompanyId
      : getFolderCompanyId(folder, selectedMeasurementDateDocuments);
    const initialMeasurementDateId = folder.measurementDateId ?? selectedMeasurementDateId;

    if (selectedMeasurementDate) {
      handleMeasurementDateSelect(selectedMeasurementDate);
    }

    setUploadingDocumentContext({
      initialCompanyId,
      initialFolderId: folder.isCompanyGroup ? null : folder.id,
      initialMeasurementDateDate: selectedMeasurementDate?.date ?? null,
      initialMeasurementDateId,
      isCompanyLocked: isCompanyDocumentsView,
    });
  }, [
    canMutateDocuments,
    handleMeasurementDateSelect,
    isCompanyDocumentsView,
    selectedCompanyId,
    selectedMeasurementDate,
    selectedMeasurementDateDocuments,
    selectedMeasurementDateId,
  ]);

  const handleDocumentDelete = useCallback((documentReference: FirmDocumentFileActionReference) => {
    if (!canMutateDocuments) {
      return;
    }

    setDeletingDocumentReferences([documentReference]);
  }, [canMutateDocuments]);

  const executeDocumentMove = useCallback((
    documentReference: FirmDocumentFileActionReference,
    {
      targetFolderId,
      targetMeasurementDateId: targetMoveMeasurementDateId,
    }: Pick<PendingMoveAction, 'targetFolderId' | 'targetMeasurementDateId'>,
    options?: { suppressErrorNotification?: boolean }
  ) => {
    if (!canMutateDocuments || !firmId || !targetMoveMeasurementDateId) {
      return Promise.resolve();
    }

    return moveDocumentReferences({
      documentReferenceIds: [documentReference.document_reference_id],
      firmId,
      suppressErrorNotification: options?.suppressErrorNotification,
      targetFolderId,
      targetMeasurementDateId: targetMoveMeasurementDateId,
    }).then(() => undefined);
  }, [
    canMutateDocuments,
    firmId,
    moveDocumentReferences,
  ]);

  const handleDocumentMove = useCallback((
    documentReference: FirmDocumentFileActionReference,
    {
      targetCompanyId,
      targetFolderId,
      targetLabel,
      targetMeasurementDateId: targetMoveMeasurementDateId,
    }: Omit<PendingMoveAction, 'documentReference'>
  ) => {
    if (!canMutateDocuments) {
      return Promise.resolve();
    }

    const sourceMeasurementDateId = getDocumentReferenceMeasurementDateId(
      documentReference,
      selectedMeasurementDateId
    );
    const sourceCompanyId = getDocumentReferenceCompanyId(documentReference) ?? selectedCompanyId;

    if (
      !targetMoveMeasurementDateId
      || (
        targetMoveMeasurementDateId === sourceMeasurementDateId
        && targetFolderId === (documentReference.folder ?? null)
      )
    ) {
      return Promise.resolve();
    }

    const isCrossScopeAllDocumentsMove = !isCompanyDocumentsView && (
      targetMoveMeasurementDateId !== sourceMeasurementDateId
        || (Boolean(targetCompanyId && sourceCompanyId) && targetCompanyId !== sourceCompanyId)
    );

    if (isCrossScopeAllDocumentsMove) {
      setMoveDialogErrorMessage(null);
      setPendingMoveAction({
        documentReference,
        targetCompanyId,
        targetFolderId,
        targetLabel,
        targetMeasurementDateId: targetMoveMeasurementDateId,
      });
      return Promise.resolve();
    }

    return executeDocumentMove(documentReference, {
      targetFolderId,
      targetMeasurementDateId: targetMoveMeasurementDateId,
    }).then(() => {
      setHighlightedDocumentReferenceId(documentReference.document_reference_id);
    });
  }, [
    executeDocumentMove,
    isCompanyDocumentsView,
    canMutateDocuments,
    selectedCompanyId,
    selectedMeasurementDateId,
  ]);

  const handleBulkActionsOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setBulkActionsAnchorEl(event.currentTarget);
  }, []);

  const handleBulkActionsClose = useCallback(() => {
    setBulkActionsAnchorEl(null);
  }, []);

  const handleBulkDownload = useCallback(() => {
    handleBulkActionsClose();
    if (!canDownloadDocuments || !selectedDocumentReferences.length) {
      return;
    }

    setDownloadingDocumentReferences(selectedDocumentReferences);
  }, [canDownloadDocuments, handleBulkActionsClose, selectedDocumentReferences]);

  const handleBulkDelete = useCallback(() => {
    handleBulkActionsClose();
    if (!canMutateDocuments || !selectedDocumentReferences.length) {
      return;
    }

    setDeletingDocumentReferences(selectedDocumentReferences);
  }, [canMutateDocuments, handleBulkActionsClose, selectedDocumentReferences]);

  const handleBulkCopy = useCallback(() => {
    handleBulkActionsClose();

    if (!canMutateDocuments || !isCompanyDocumentsView || !selectedDocumentReferences.length) {
      return;
    }

    setInitialCopyTargetMeasurementDateId(null);
    setCopyDialogErrorMessage(null);
    setCopyingDocumentReferences(selectedDocumentReferences);
  }, [canMutateDocuments, handleBulkActionsClose, isCompanyDocumentsView, selectedDocumentReferences]);

  const handleBulkAnalyze = useCallback((processingType: FirmDocumentAnalyzeProcessingType) => {
    handleBulkActionsClose();

    if (!canMutateDocuments || !isCompanyDocumentsView || !selectedCompanyId || !selectedDocumentReferences.length) {
      return;
    }

    setPendingAnalyzeAction({
      companyId: selectedCompanyId,
      documentReferences: selectedDocumentReferences,
      processingType,
    });
  }, [
    canMutateDocuments,
    handleBulkActionsClose,
    isCompanyDocumentsView,
    selectedCompanyId,
    selectedDocumentReferences,
  ]);

  const handleAnalyzeDialogClose = useCallback(() => {
    if (!isAnalyzingDocuments) {
      setPendingAnalyzeAction(null);
    }
  }, [isAnalyzingDocuments]);

  const handleAnalyzeConfirm = useCallback(() => {
    if (!pendingAnalyzeAction) {
      return;
    }

    const fileIds = pendingAnalyzeAction.documentReferences
      .map(documentReference => documentReference.file.id)
      .filter((fileId): fileId is number => typeof fileId === 'number');

    if (!fileIds.length) {
      setPendingAnalyzeAction(null);
      return;
    }

    analyzeDocuments({
      companyId: pendingAnalyzeAction.companyId,
      fileIds,
      processingType: pendingAnalyzeAction.processingType,
    })
      .then(() => {
        clearSelectedDocumentReferences(pendingAnalyzeAction.documentReferences.map(
          documentReference => documentReference.document_reference_id
        ));
        setPendingAnalyzeAction(null);
      })
      .catch(() => undefined);
  }, [analyzeDocuments, clearSelectedDocumentReferences, pendingAnalyzeAction]);

  const handleCopyDialogClose = useCallback(() => {
    if (!isCopyingDocumentReferences) {
      setCopyingDocumentReferences(EMPTY_DOCUMENT_ACTION_REFERENCES);
      setInitialCopyTargetMeasurementDateId(null);
      setCopyDialogErrorMessage(null);
    }
  }, [isCopyingDocumentReferences]);

  const handleCopyConfirm = useCallback((copyTargetMeasurementDate: FirmDocumentCopyTargetMeasurementDate) => {
    if (!firmId || !copyingDocumentReferences.length) {
      return;
    }

    setCopyDialogErrorMessage(null);
    copyDocumentReferences({
      documentReferenceIds: copyingDocumentReferences.map(
        documentReference => documentReference.document_reference_id
      ),
      firmId,
      suppressErrorNotification: true,
      targetMeasurementDateId: copyTargetMeasurementDate.id,
    })
      .then(response => {
        const copiedDocumentReferenceId = response.document_references[0]?.document_reference_id ?? null;

        clearSelectedDocumentReferences(copyingDocumentReferences.map(
          documentReference => documentReference.document_reference_id
        ));
        delete measurementDateDocumentsByIdRef.current[copyTargetMeasurementDate.id];
        setHighlightedDocumentReferenceId(copiedDocumentReferenceId);
        setPreferredMeasurementDateId(copyTargetMeasurementDate.id);
        setMeasurementDateQueryValue(copyTargetMeasurementDate.date, { replace: true });
        setCopyingDocumentReferences(EMPTY_DOCUMENT_ACTION_REFERENCES);
        setInitialCopyTargetMeasurementDateId(null);
      })
      .catch(error => {
        setCopyDialogErrorMessage(getDocumentTransferErrorMessage({
          actionPastTense: 'copied',
          defaultMessage: DOCUMENT_TRANSFER_COPY_FALLBACK_ERROR,
          documentCount: copyingDocumentReferences.length,
          error,
        }));
      });
  }, [
    clearSelectedDocumentReferences,
    copyDocumentReferences,
    copyingDocumentReferences,
    firmId,
    setMeasurementDateQueryValue,
  ]);

  const handleMoveDialogClose = useCallback(() => {
    if (!isMovingDocumentReferences) {
      setPendingMoveAction(null);
      setMoveDialogErrorMessage(null);
    }
  }, [isMovingDocumentReferences]);

  const handleMoveConfirm = useCallback(() => {
    if (!pendingMoveAction) {
      return;
    }

    setMoveDialogErrorMessage(null);
    executeDocumentMove(pendingMoveAction.documentReference, {
      targetFolderId: pendingMoveAction.targetFolderId,
      targetMeasurementDateId: pendingMoveAction.targetMeasurementDateId,
    }, {
      suppressErrorNotification: true,
    })
      .then(() => {
        clearSelectedDocumentReferences([pendingMoveAction.documentReference.document_reference_id]);
        setHighlightedDocumentReferenceId(pendingMoveAction.documentReference.document_reference_id);
        setPendingMoveAction(null);
      })
      .catch(error => {
        setMoveDialogErrorMessage(getDocumentTransferErrorMessage({
          actionPastTense: 'moved',
          defaultMessage: DOCUMENT_TRANSFER_MOVE_FALLBACK_ERROR,
          documentCount: 1,
          error,
        }));
      });
  }, [clearSelectedDocumentReferences, executeDocumentMove, pendingMoveAction]);

  const handleDownloadDialogClose = useCallback(() => {
    if (!isDownloadingDocuments) {
      setDownloadingDocumentReferences(EMPTY_DOCUMENT_ACTION_REFERENCES);
    }
  }, [isDownloadingDocuments]);

  const handleDownloadConfirm = useCallback(() => {
    if (!downloadingDocumentReferences.length) {
      return;
    }

    const downloadTargets = downloadingDocumentReferences
      .map(documentReference => getFirmDocumentDownloadTarget(documentReference, firmId))
      .filter(isFirmDocumentDownloadTarget);

    if (!downloadTargets.length) {
      setDownloadingDocumentReferences(EMPTY_DOCUMENT_ACTION_REFERENCES);
      return;
    }

    downloadDocuments({ documents: downloadTargets })
      .then(() => {
        clearSelectedDocumentReferences(downloadingDocumentReferences.map(
          documentReference => documentReference.document_reference_id
        ));
        setDownloadingDocumentReferences(EMPTY_DOCUMENT_ACTION_REFERENCES);
      })
      .catch(() => undefined);
  }, [clearSelectedDocumentReferences, downloadDocuments, downloadingDocumentReferences, firmId]);

  const handleDeleteDialogClose = useCallback(() => {
    if (!isDeletingDocumentReferences) {
      setDeletingDocumentReferences(EMPTY_DOCUMENT_ACTION_REFERENCES);
    }
  }, [isDeletingDocumentReferences]);

  const handleFolderDeleteDialogClose = useCallback(() => {
    if (!isDeletingFolder) {
      setDeletingFolder(null);
    }
  }, [isDeletingFolder]);

  const handleFolderDeleteConfirm = useCallback(() => {
    if (!deletingFolder) {
      return;
    }

    deleteFolder({
      firmId,
      folderId: deletingFolder.folderId,
      folderName: deletingFolder.folderName,
      mdDocumentsId: deletingFolder.mdDocumentsId,
    })
      .then(() => {
        setDeletingFolder(null);
      })
      .catch(() => undefined);
  }, [deleteFolder, deletingFolder, firmId]);

  const handleFolderCreateDialogClose = useCallback(() => {
    if (!isCreatingFolder) {
      setCreatingFolder(null);
    }
  }, [isCreatingFolder]);

  const handleFolderCreateConfirm = useCallback((values: FirmDocumentFolderCreateDialogValues) => {
    if (!creatingFolder) {
      return;
    }

    createFolder({
      firmId,
      folderName: values.folderName,
      mdDocumentsId: values.mdDocumentsId,
      parentFolderId: values.parentFolderId,
    })
      .then(createdFolder => {
        if (createdFolder.id) {
          setHighlightedFolderId(createdFolder.id);
        }

        setCreatingFolder(null);
      })
      .catch(() => undefined);
  }, [createFolder, creatingFolder, firmId]);

  const handleDocumentEditDialogClose = useCallback(() => {
    if (!isEditingDocumentReference) {
      setEditingDocumentReference(null);
    }
  }, [isEditingDocumentReference]);

  const handleDocumentEditConfirm = useCallback((values: FirmDocumentEditDialogValues) => {
    if (!firmId || !editingDocumentReference || !editingDocumentCompanyId || !editingDocumentMeasurementDateId) {
      return;
    }

    const editedDocumentReferenceId = editingDocumentReference.document_reference_id;

    editDocumentReference({
      companyId: editingDocumentCompanyId,
      documentReferenceId: editedDocumentReferenceId,
      fileName: values.fileName,
      firmId,
      measurementDateId: editingDocumentMeasurementDateId,
      referenceObjectTypes: values.referenceObjectTypes,
      requestedFileIds: values.requestedFileIds,
    })
      .then(() => {
        setHighlightedDocumentReferenceId(editedDocumentReferenceId);
        setEditingDocumentReference(null);
      })
      .catch(() => undefined);
  }, [
    editDocumentReference,
    editingDocumentCompanyId,
    editingDocumentMeasurementDateId,
    editingDocumentReference,
    firmId,
  ]);

  const handleFolderEditDialogClose = useCallback(() => {
    if (!isRenamingFolder) {
      setEditingFolder(null);
    }
  }, [isRenamingFolder]);

  const handleFolderEditConfirm = useCallback((values: FirmDocumentFolderEditDialogValues) => {
    if (!editingFolder) {
      return;
    }

    renameFolder({
      firmId,
      folderId: editingFolder.folderId,
      folderName: values.folderName,
      mdDocumentsId: editingFolder.mdDocumentsId,
      parentFolderId: values.parentFolderId,
    })
      .then(() => {
        setHighlightedFolderId(editingFolder.folderId);
        setEditingFolder(null);
      })
      .catch(() => undefined);
  }, [editingFolder, firmId, renameFolder]);

  const handleUploadDialogClose = useCallback(() => {
    if (!isUploadDialogBusy) {
      clearUploadDialogState();
    }
  }, [clearUploadDialogState, isUploadDialogBusy]);

  const handleUploadConfirm = useCallback((values: FirmDocumentUploadDialogValues) => {
    if (!firmId || isUploadingDocumentBatch) {
      return;
    }

    const totalFiles = values.files.length;

    setIsUploadingDocumentBatch(true);
    setIsUploadComplete(false);
    setUploadProgress({
      completedFiles: 0,
      currentFileIndex: 0,
      totalFiles,
    });

    values.files.reduce<Promise<number | null>>(
      (previousUpload, file, fileIndex) => previousUpload.then(lastUploadedDocumentReferenceId => {
        const isLastFile = fileIndex === totalFiles - 1;

        setUploadProgress({
          completedFiles: fileIndex,
          currentFileIndex: fileIndex,
          totalFiles,
        });

        return uploadDocument({
          file,
          firmId,
          folderId: values.folderId,
          mdDocumentsId: values.mdDocumentsId,
          measurementDateId: values.measurementDateId,
          suppressQueryInvalidation: !isLastFile,
          suppressSuccessNotification: true,
        }).then(response => {
          setUploadProgress({
            completedFiles: fileIndex + 1,
            currentFileIndex: fileIndex,
            totalFiles,
          });

          return response.document_reference_id ?? lastUploadedDocumentReferenceId;
        });
      }),
      Promise.resolve(null)
    )
      .then(lastUploadedDocumentReferenceId => {
        if (lastUploadedDocumentReferenceId) {
          setHighlightedDocumentReferenceId(lastUploadedDocumentReferenceId);
        }

        successNotification(
          totalFiles === 1
            ? `${values.files[0].name} was uploaded successfully.`
            : `${totalFiles} documents uploaded successfully.`
        );
        setPreferredMeasurementDateId(values.measurementDateId);
        setMeasurementDateQueryValue(values.measurementDateDate, { replace: true });
        setIsUploadComplete(true);
      })
      .catch(() => {
        setIsUploadComplete(false);
        setUploadProgress(null);
      })
      .finally(() => setIsUploadingDocumentBatch(false));
  }, [firmId, isUploadingDocumentBatch, setMeasurementDateQueryValue, successNotification, uploadDocument]);

  const handleDeleteConfirm = useCallback(() => {
    if (!firmId || !deletingDocumentReferences.length) {
      return;
    }

    const documentReferenceIds = deletingDocumentReferences.map(
      documentReference => documentReference.document_reference_id
    );

    deleteDocumentReferences({ documentReferenceIds, firmId })
      .then(response => {
        clearSelectedDocumentReferences(response.deleted_document_reference_ids);
        setDeletingDocumentReferences(EMPTY_DOCUMENT_ACTION_REFERENCES);
      })
      .catch(() => undefined);
  }, [clearSelectedDocumentReferences, deleteDocumentReferences, deletingDocumentReferences, firmId]);

  const handleMeasurementDateSelectionChange = useCallback((
    measurementDate: FirmDocumentMeasurementDate,
    checked: boolean
  ) => {
    const measurementDateId = measurementDate.id;

    if (!measurementDateId) {
      return;
    }

    if (measurementDateId !== selectedMeasurementDateId) {
      setPendingMeasurementDateSelection({ checked, measurementDateId });
      handleMeasurementDateSelect(measurementDate);
      return;
    }

    setSelectedDocumentReferenceIdsByMeasurementDateId(currentSelection => ({
      ...currentSelection,
      [measurementDateId]: checked ? activeMeasurementDateDocumentReferenceIds : EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS,
    }));
  }, [activeMeasurementDateDocumentReferenceIds, handleMeasurementDateSelect, selectedMeasurementDateId]);

  const getMeasurementDateSelectionState = useCallback((measurementDate: FirmDocumentMeasurementDate) => {
    if (!measurementDate.id) {
      return {
        checked: false,
        disabled: true,
        indeterminate: false,
      };
    }

    if (measurementDate.id !== selectedMeasurementDateId) {
      const selectedCount = selectedDocumentReferenceIdsByMeasurementDateId[measurementDate.id]?.length ?? 0;

      return {
        checked: false,
        disabled: !canUseDocumentSelection,
        indeterminate: selectedCount > 0,
      };
    }

    const selectableCount = activeMeasurementDateDocumentReferenceIds.length;
    const selectedCount = activeMeasurementDateDocumentReferenceIds.filter(documentReferenceId =>
      selectedDocumentReferenceIds.has(documentReferenceId)
    ).length;

    return {
      checked: selectableCount > 0 && selectedCount === selectableCount,
      disabled: !canUseDocumentSelection || selectableCount === 0 || isMeasurementDateDocumentsLoading,
      indeterminate: selectedCount > 0 && selectedCount < selectableCount,
    };
  }, [
    activeMeasurementDateDocumentReferenceIds,
    canUseDocumentSelection,
    isMeasurementDateDocumentsLoading,
    selectedDocumentReferenceIdsByMeasurementDateId,
    selectedDocumentReferenceIds,
    selectedMeasurementDateId,
  ]);

  const renderMeasurementDateDocuments = useCallback((measurementDate: FirmDocumentMeasurementDate) => {
    const measurementDateId = measurementDate.id ?? null;
    const isActiveMeasurementDate = measurementDateId === selectedMeasurementDateId;
    let measurementDateDocuments: CompanyDocuments | null = null;

    if (isActiveMeasurementDate) {
      measurementDateDocuments = (isCompanyDocumentsView
        ? companyLevelMeasurementDateDocuments
        : firmLevelMeasurementDateDocuments) ?? (
        measurementDateId ? measurementDateDocumentsByIdRef.current[measurementDateId] ?? null : null
      );
    } else if (measurementDateId) {
      measurementDateDocuments = measurementDateDocumentsByIdRef.current[measurementDateId] ?? null;
    }

    const measurementDateSelectedDocumentReferenceIds = new Set(
      measurementDateId
        ? selectedDocumentReferenceIdsByMeasurementDateId[measurementDateId] ?? EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS
        : EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS
    );

    return (
      <MeasurementDateDocumentsTree
        actionInProgress={isDocumentActionInProgress}
        canCopyDocuments={canMutateDocuments && isCompanyDocumentsView && activeMeasurementDates.length > 1}
        canDownloadDocuments={canDownloadDocuments}
        canManageDocuments={canMutateDocuments}
        canMoveDocuments={canMutateDocuments && isActiveMeasurementDate && Boolean(selectedMeasurementDateId)}
        canSelectDocuments={canUseDocumentSelection}
        documents={measurementDateDocuments}
        downloadingDocumentReferenceId={downloadingDocumentReferenceId}
        highlightedDocumentReferenceId={highlightedDocumentReferenceId}
        highlightedFolderId={highlightedFolderId}
        hasMoreDocuments={isActiveMeasurementDate && hasMoreDocuments}
        isError={isActiveMeasurementDate && (
          firmDocumentsQuery.isError || firmDocumentFoldersQuery.isError
        )}
        isLoading={isActiveMeasurementDate && isMeasurementDateDocumentsLoading}
        isShowMoreDocumentsLoading={isActiveMeasurementDate && isFetchingNextDocumentsPage}
        onDocumentCopy={canMutateDocuments && isCompanyDocumentsView ? handleDocumentCopy : undefined}
        onDocumentCopyToMeasurementDate={
          canMutateDocuments && isCompanyDocumentsView ? handleDocumentCopyToMeasurementDate : undefined
        }
        onDocumentDelete={handleDocumentDelete}
        onDocumentDownload={handleDocumentDownload}
        onDocumentMove={canMutateDocuments ? handleDocumentMove : undefined}
        onDocumentReferencesSelectionChange={handleDocumentReferencesSelectionChange}
        onDocumentRename={handleDocumentRename}
        onDocumentSelectionChange={handleDocumentSelectionChange}
        onFolderAddSubfolder={canMutateDocuments ? handleFolderAddSubfolder : undefined}
        onFolderDelete={canMutateDocuments ? handleFolderDelete : undefined}
        onFolderRename={canMutateDocuments ? handleFolderRename : undefined}
        onFolderUploadDocument={canMutateDocuments ? handleFolderUploadDocument : undefined}
        onEmptyUploadDocument={
          canMutateDocuments && isActiveMeasurementDate && !hasActiveDocumentSearchOrFilters
            ? () => handleMeasurementDateUploadDocument(measurementDate)
            : undefined
        }
        onShowMoreDocuments={isActiveMeasurementDate ? handleShowMoreDocuments : undefined}
        ordering={documentFilterValue.ordering}
        remainingDocumentCount={isActiveMeasurementDate ? remainingDocumentCount : 0}
        searchHighlightText={documentSearchValue}
        selectedDocumentReferenceIds={measurementDateSelectedDocumentReferenceIds}
        showEmptyFolders={!hasActiveDocumentSearchOrFilters}
      />
    );
  }, [
    activeMeasurementDates.length,
    canDownloadDocuments,
    canMutateDocuments,
    canUseDocumentSelection,
    companyLevelMeasurementDateDocuments,
    documentSearchValue,
    firmDocumentFoldersQuery.isError,
    firmDocumentsQuery.isError,
    firmLevelMeasurementDateDocuments,
    handleDocumentDelete,
    handleDocumentCopy,
    handleDocumentCopyToMeasurementDate,
    handleDocumentDownload,
    handleDocumentMove,
    handleDocumentReferencesSelectionChange,
    handleDocumentRename,
    handleDocumentSelectionChange,
    handleFolderAddSubfolder,
    handleFolderDelete,
    handleFolderRename,
    handleFolderUploadDocument,
    handleMeasurementDateUploadDocument,
    handleShowMoreDocuments,
    highlightedDocumentReferenceId,
    highlightedFolderId,
    hasActiveDocumentSearchOrFilters,
    downloadingDocumentReferenceId,
    documentFilterValue.ordering,
    hasMoreDocuments,
    isCompanyDocumentsView,
    isDocumentActionInProgress,
    isFetchingNextDocumentsPage,
    isMeasurementDateDocumentsLoading,
    remainingDocumentCount,
    selectedDocumentReferenceIdsByMeasurementDateId,
    selectedMeasurementDateId,
  ]);

  const tertiaryNavigation = useMemo(
    () => ({
      actions: [
        {
          checked: isSelectAllDocumentsChecked,
          disabled: isSelectAllDocumentsDisabled,
          indeterminate: isSelectAllDocumentsIndeterminate,
          inputAriaLabel: FIRM_DOCUMENTS_LABELS.selectAllDocuments,
          key: 'select-all-documents',
          label: FIRM_DOCUMENTS_LABELS.selectAllDocuments,
          onChange: handleSelectAllDocumentsChange,
          testId: FIRM_DOCUMENTS_TEST_IDS.selectAllDocuments,
          type: 'checkbox' as const,
        },
        {
          ariaLabel: FIRM_DOCUMENTS_LABELS.findDocuments,
          collapsedAriaLabel: FIRM_DOCUMENTS_LABELS.findDocuments,
          collapsedTooltipTitle: FIRM_DOCUMENTS_LABELS.findDocuments,
          clearAriaLabel: FIRM_DOCUMENTS_LABELS.clearDocumentSearch,
          commitMode: 'submit' as const,
          collapsible: true,
          key: 'find-document',
          isSuggestionsLoading: isDocumentSuggestionsLoading,
          onChange: handleDocumentSearchSubmit,
          onInputValueChange: handleDocumentSearchInputValueChange,
          onSuggestionSelect: handleDocumentSearchSuggestionSelect,
          placeholder: selectedCompany
            ? FIRM_DOCUMENTS_LABELS.findDocumentsInCompany(selectedCompany.name)
            : FIRM_DOCUMENTS_LABELS.findDocumentsInAllDocuments,
          showEmptySuggestions: firmDocumentSuggestionsQuery.isSuccess
            && isDocumentSuggestionInputReady
            && isDocumentSuggestionSearchSettled
            && documentSearchSuggestions.length === 0,
          submitAriaLabel: FIRM_DOCUMENTS_LABELS.submitDocumentSearch,
          suggestions: documentSearchSuggestions,
          suggestionsEmptyLabel: FIRM_DOCUMENTS_LABELS.noSearchSuggestions,
          suggestionsLoadingLabel: FIRM_DOCUMENTS_LABELS.searchSuggestionsLoading,
          testId: FIRM_DOCUMENTS_TEST_IDS.documentSearchInput,
          type: 'search' as const,
          value: documentSearchValue,
        },
        {
          ariaLabel: activeDocumentFilterCount > 0
            ? FIRM_DOCUMENTS_LABELS.sortAndFilterWithCount(activeDocumentFilterCount)
            : FIRM_DOCUMENTS_LABELS.sortAndFilter,
          icon: (
            <FirmDocumentsFilterActionBadge
              badgeContent={activeDocumentFilterCount}
              invisible={activeDocumentFilterCount === 0}
              overlap="circular">
              <FilterList fontSize="inherit" />
            </FirmDocumentsFilterActionBadge>
          ),
          key: 'filter-documents',
          onClick: handleDocumentFilterOpen,
          testId: FIRM_DOCUMENTS_TEST_IDS.filterButton,
          tooltipTitle: documentFilterTooltipTitle,
          type: 'iconButton' as const,
        },
        {
          ariaLabel: fullscreenActionLabel,
          icon: <ZoomOutMap fontSize="inherit" />,
          key: 'fullscreen-documents',
          onClick: handleFullscreenToggle,
          testId: FIRM_DOCUMENTS_TEST_IDS.fullscreenButton,
          tooltipTitle: fullscreenActionLabel,
          type: 'iconButton' as const,
        },
        ...(shouldRenderBulkActions ? [{
          disabled: !isBulkActionsVisible || !canUseDocumentSelection || isDocumentActionInProgress,
          endIcon: <MoreVert fontSize="small" />,
          hasPopup: true,
          isVisible: isBulkActionsVisible,
          key: 'bulk-actions',
          label: (
            <FirmDocumentsBulkActionEndIcon>
              <span>{FIRM_DOCUMENTS_LABELS.bulkActions}</span>
              <span
                aria-hidden="true"
                data-firm-documents-bulk-action-count="true">
                ({bulkActionDocumentCount})
              </span>
            </FirmDocumentsBulkActionEndIcon>
          ),
          onClick: handleBulkActionsOpen,
          testId: FIRM_DOCUMENTS_TEST_IDS.bulkActionsTrigger,
          type: 'button' as const,
          variant: 'secondary' as const,
        }] : []),
      ],
      ariaLabel: FIRM_DOCUMENTS_LABELS.tertiaryNavigationAriaLabel,
      items: [
        {
          label: FIRM_DOCUMENTS_LABELS.allDocuments,
          onSelect: () => navigateToDocumentsView(null),
          value: ALL_DOCUMENTS_TAB_VALUE,
        },
        ...openedCompanyTabs.map(company => ({
          closeAriaLabel: `Close ${company.name} tab`,
          isClosable: true,
          label: company.name,
          onClose: () => handleCompanyTabClose(company.id),
          onSelect: () => navigateToDocumentsView(company.slug),
          value: getCompanyTabValue(company.slug),
        })),
      ],
      value: activeDocumentsViewValue,
    }),
    [
      activeDocumentsViewValue,
      activeDocumentFilterCount,
      bulkActionDocumentCount,
      canUseDocumentSelection,
      documentFilterTooltipTitle,
      documentSearchSuggestions,
      documentSearchValue,
      firmDocumentSuggestionsQuery.isSuccess,
      fullscreenActionLabel,
      handleBulkActionsOpen,
      handleCompanyTabClose,
      handleDocumentFilterOpen,
      handleDocumentSearchInputValueChange,
      handleDocumentSearchSuggestionSelect,
      handleDocumentSearchSubmit,
      handleFullscreenToggle,
      handleSelectAllDocumentsChange,
      isBulkActionsVisible,
      isDocumentActionInProgress,
      isDocumentSuggestionInputReady,
      isDocumentSuggestionSearchSettled,
      isDocumentSuggestionsLoading,
      isSelectAllDocumentsChecked,
      isSelectAllDocumentsDisabled,
      isSelectAllDocumentsIndeterminate,
      navigateToDocumentsView,
      openedCompanyTabs,
      selectedCompany,
      shouldRenderBulkActions,
    ]
  );

  // Cache the active view's URL-owned context so tab clicks can restore it before the next view renders.
  useEffect(() => {
    documentsViewScopedQueryByScopeRef.current.set(
      getDocumentsViewQueryScope(selectedCompanySlug),
      getDocumentsViewScopedQuery(searchParams)
    );
  }, [searchParams, selectedCompanySlug]);

  // The URL owns the active company tab so Documents can be deep-linked and restored after refresh.
  useEffect(() => {
    if (!selectedCompanySlug || !selectedCompany) {
      return;
    }

    setOpenedCompanyTabs(currentTabs => {
      if (currentTabs.some(tab => tab.slug === selectedCompany.slug)) {
        return currentTabs;
      }

      return [...currentTabs, selectedCompany];
    });
  }, [selectedCompany, selectedCompanySlug]);

  // If a shared Documents URL points to a company slug that no longer exists, fall back to All Documents.
  useEffect(() => {
    if (
      !selectedCompanySlug
      || selectedCompany
      || !firmDocumentCompanyMetadataQuery.data
      || firmDocumentCompanyMetadataQuery.isFetching
    ) {
      return;
    }

    navigateToDocumentsView(null, { replace: true });
  }, [
    firmDocumentCompanyMetadataQuery.data,
    firmDocumentCompanyMetadataQuery.isFetching,
    navigateToDocumentsView,
    selectedCompany,
    selectedCompanySlug,
  ]);

  // Keep opened company tabs hydrated with the latest backend-backed name/icon metadata after refetch.
  useEffect(() => {
    setOpenedCompanyTabs(currentTabs => {
      if (!currentTabs.length) {
        return currentTabs;
      }

      return currentTabs.map(tab => companyMetadata.find(company => company.id === tab.id) ?? tab);
    });
  }, [companyMetadata]);

  // A firm switch should not preserve company tabs from the previous firm workspace.
  useEffect(() => {
    const previousFirmId = previousFirmIdRef.current;
    previousFirmIdRef.current = firmId;

    if (!previousFirmId || previousFirmId === firmId) {
      return;
    }

    setOpenedCompanyTabs([]);
    setHighlightedDocumentReferenceId(null);
    setHighlightedFolderId(null);
    setSelectedCompanySlug(null, { replace: true });
    setSelectedDocumentReferenceIdsByMeasurementDateId({});
    setPendingMeasurementDateSelection(null);
    setPendingMeasurementDateAddFolder(null);
    setPendingMeasurementDateKey(null);
    setBulkActionsAnchorEl(null);
    setDeletingDocumentReferences(EMPTY_DOCUMENT_ACTION_REFERENCES);
    setDownloadingDocumentReferences(EMPTY_DOCUMENT_ACTION_REFERENCES);
    setCopyingDocumentReferences(EMPTY_DOCUMENT_ACTION_REFERENCES);
    setCopyDialogErrorMessage(null);
    setCreatingFolder(null);
    setEditingDocumentReference(null);
    setEditingFolder(null);
    setInitialCopyTargetMeasurementDateId(null);
    setPendingAnalyzeAction(null);
    setPendingMoveAction(null);
    setMoveDialogErrorMessage(null);
    setUploadingDocumentContext(null);
    documentsViewScopedQueryByScopeRef.current.clear();
    measurementDateDocumentsByIdRef.current = {};
  }, [firmId, setSelectedCompanySlug]);

  // Company tab switches should clear document caches and selections owned by the previous view.
  useEffect(() => {
    measurementDateDocumentsByIdRef.current = {};
    setSelectedDocumentReferenceIdsByMeasurementDateId({});
    setPendingMeasurementDateSelection(null);
    setPendingMeasurementDateAddFolder(null);
    setPendingMeasurementDateKey(null);
    setInitialCopyTargetMeasurementDateId(null);
    setCopyDialogErrorMessage(null);
    setCreatingFolder(null);
    setEditingDocumentReference(null);
    setEditingFolder(null);
    setPendingMoveAction(null);
    setMoveDialogErrorMessage(null);
    setUploadingDocumentContext(null);
  }, [selectedCompanySlug]);

  // Search/filter changes invalidate cached measurement-date document rows and selections.
  useEffect(() => {
    measurementDateDocumentsByIdRef.current = {};
    setSelectedDocumentReferenceIdsByMeasurementDateId({});
    setPendingMeasurementDateSelection(null);
    setPendingMeasurementDateAddFolder(null);
    setPendingMeasurementDateKey(null);
    setCopyDialogErrorMessage(null);
    setCreatingFolder(null);
    setEditingDocumentReference(null);
    setEditingFolder(null);
    setPendingMoveAction(null);
    setMoveDialogErrorMessage(null);
    setUploadingDocumentContext(null);
  }, [documentFilterSignature]);

  // Keep local search input and suggestion state aligned with URL-driven search changes.
  useEffect(() => {
    setDocumentSearchInputValue(documentSearchValue);
    setDocumentSuggestionSearchValue(documentSearchValue);
  }, [documentSearchValue]);

  // Mirror fullscreen browser state into React so the tertiary action label and page chrome stay in sync.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    handleFullscreenChange();
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Only clear stale dates for unfiltered scope changes. Search/filter results may legitimately omit the selected date.
  useEffect(() => {
    if (
      !hasMeasurementDateTarget
      || hasActiveDocumentSearchOrFilters
      || isTargetMeasurementDateFound
      || !firmDocumentMeasurementDatesQuery.data
      || firmDocumentMeasurementDatesQuery.isFetching
    ) {
      return;
    }

    setMeasurementDateQueryValue(null, { replace: true });
  }, [
    firmDocumentMeasurementDatesQuery.data,
    firmDocumentMeasurementDatesQuery.isFetching,
    hasActiveDocumentSearchOrFilters,
    hasMeasurementDateTarget,
    isTargetMeasurementDateFound,
    setMeasurementDateQueryValue,
  ]);

  // Clear the pending measurement-date loader once the requested date has loaded or is cached.
  useEffect(() => {
    if (
      !pendingMeasurementDateKey
      || pendingMeasurementDateKey !== selectedMeasurementDateKey
      || isMeasurementDateDocumentsLoading
    ) {
      return;
    }

    if (hasSelectedMeasurementDateDocumentsResponse || selectedMeasurementDateCachedDocuments) {
      setPendingMeasurementDateKey(null);
    }
  }, [
    hasSelectedMeasurementDateDocumentsResponse,
    isMeasurementDateDocumentsLoading,
    pendingMeasurementDateKey,
    selectedMeasurementDateCachedDocuments,
    selectedMeasurementDateKey,
  ]);

  // Apply a delayed select-all request after its target measurement date finishes loading.
  useEffect(() => {
    if (
      !pendingMeasurementDateSelection
      || pendingMeasurementDateSelection.measurementDateId !== selectedMeasurementDateId
      || isMeasurementDateDocumentsLoading
    ) {
      return;
    }

    setSelectedDocumentReferenceIdsByMeasurementDateId(currentSelection => ({
      ...currentSelection,
      [pendingMeasurementDateSelection.measurementDateId]: pendingMeasurementDateSelection.checked
        ? activeMeasurementDateDocumentReferenceIds
        : EMPTY_SELECTED_DOCUMENT_REFERENCE_IDS,
    }));
    setPendingMeasurementDateSelection(null);
  }, [
    activeMeasurementDateDocumentReferenceIds,
    isMeasurementDateDocumentsLoading,
    pendingMeasurementDateSelection,
    selectedMeasurementDateId,
  ]);

  // Open deferred Add Folder actions after a non-active measurement date is selected and its bucket loads.
  useEffect(() => {
    if (
      !pendingMeasurementDateAddFolder
      || pendingMeasurementDateAddFolder.measurementDateKey !== selectedMeasurementDateKey
      || isMeasurementDateDocumentsLoading
    ) {
      return;
    }

    const didOpenDialog = openMeasurementDateAddFolderDialog(pendingMeasurementDateAddFolder.measurementDate);
    const didResolveUnavailableTarget = isCompanyDocumentsView
      ? selectedMeasurementDateDocumentsQuery.dataUpdatedAt > 0 || selectedMeasurementDateDocumentsQuery.isError
      : hasSelectedMeasurementDateDocumentsResponse;

    if (didOpenDialog || didResolveUnavailableTarget) {
      setPendingMeasurementDateAddFolder(null);
    }
  }, [
    hasSelectedMeasurementDateDocumentsResponse,
    isCompanyDocumentsView,
    isMeasurementDateDocumentsLoading,
    openMeasurementDateAddFolderDialog,
    pendingMeasurementDateAddFolder,
    selectedMeasurementDateDocumentsQuery.dataUpdatedAt,
    selectedMeasurementDateDocumentsQuery.isError,
    selectedMeasurementDateKey,
  ]);

  // Cache loaded document rows by measurement date so accordion switches can reuse prior responses.
  useEffect(() => {
    const measurementDateDocuments = isCompanyDocumentsView
      ? companyLevelMeasurementDateDocuments
      : firmLevelMeasurementDateDocuments;

    if (!selectedMeasurementDateId || !measurementDateDocuments) {
      return;
    }

    measurementDateDocumentsByIdRef.current[selectedMeasurementDateId] = measurementDateDocuments;
  }, [
    companyLevelMeasurementDateDocuments,
    firmLevelMeasurementDateDocuments,
    isCompanyDocumentsView,
    selectedMeasurementDateId,
  ]);

  // Keep copied or navigated documents highlighted briefly after they scroll into view.
  useEffect(() => {
    if (!highlightedDocumentReferenceId || !isHighlightedDocumentVisible) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setHighlightedDocumentReferenceId(null);
    }, 6000);

    return () => window.clearTimeout(timeoutId);
  }, [highlightedDocumentReferenceId, isHighlightedDocumentVisible]);

  // Keep edited folders highlighted briefly after their modal closes.
  useEffect(() => {
    if (!highlightedFolderId) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setHighlightedFolderId(null);
    }, 6000);

    return () => window.clearTimeout(timeoutId);
  }, [highlightedFolderId]);

  useTertiaryNavigationRegistration(tertiaryNavigation);

  const emptyDocumentsTitle = hasActiveDocumentSearchOrFilters
    ? FIRM_DOCUMENTS_LABELS.noMatchingDocumentsTitle
    : FIRM_DOCUMENTS_LABELS.noDocumentsAvailableTitle;
  const emptyDocumentsDescription = hasActiveDocumentSearchOrFilters
    ? FIRM_DOCUMENTS_LABELS.noMatchingDocumentsDescription
    : FIRM_DOCUMENTS_LABELS.noDocumentsAvailableDescription;
  const documentsPanelTitle = selectedCompany?.name ?? FIRM_DOCUMENTS_LABELS.allDocuments;

  const centerPane = (
    <MeasurementDatesAccordionPanel
      activeMeasurementDateKey={activeMeasurementDateKey}
      canAddFolder={canMutateDocuments}
      canUploadDocument={canMutateDocuments}
      emptyDescription={emptyDocumentsDescription}
      emptyTitle={emptyDocumentsTitle}
      getMeasurementDateSelectionState={getMeasurementDateSelectionState}
      hasNewerMeasurementDates={Boolean(hasNewerMeasurementDates)}
      hasMoreMeasurementDates={Boolean(hasMoreMeasurementDates)}
      isError={firmDocumentMeasurementDatesQuery.isError}
      isDocumentCopyDropTargetEnabled={isCompanyDocumentsView}
      isActionInProgress={isDocumentActionInProgress}
      isLoading={isMeasurementDatesLoading}
      isShowMoreLoading={isFetchingNextMeasurementDatesPage}
      isShowNewerLoading={isFetchingPreviousMeasurementDatesPage}
      measurementDates={activeMeasurementDates}
      onMeasurementDateAddFolder={canMutateDocuments ? handleMeasurementDateAddFolder : undefined}
      onMeasurementDateUploadDocument={canMutateDocuments ? handleMeasurementDateUploadDocument : undefined}
      onMeasurementDateSelectionChange={handleMeasurementDateSelectionChange}
      onMeasurementDateSelect={handleMeasurementDateSelect}
      onShowMoreMeasurementDates={handleShowMoreMeasurementDates}
      onShowNewerMeasurementDates={handleShowNewerMeasurementDates}
      pendingMeasurementDateKey={pendingAccordionMeasurementDateKey}
      renderMeasurementDateContent={renderMeasurementDateDocuments}
      remainingNewerMeasurementDateCount={remainingNewerMeasurementDateCount}
      remainingMeasurementDateCount={remainingMeasurementDateCount}
      title={documentsPanelTitle}
    />
  );

  return (
    <PrismThemeProvider>
      <FirmDocumentsRoot data-testid={FIRM_DOCUMENTS_TEST_IDS.root}>
        <WorkspacePaneLayout
          center={centerPane}
          defaultLeftWidth={FIRM_DOCUMENTS_PANE_WIDTHS.defaultCompanyListWidth}
          left={(
            <AllCompaniesPanel
              availableFunds={availableFunds}
              companies={companies}
              isError={firmDocumentCompaniesQuery.isError}
              isLoading={isCompanyListLoading}
              onCompanySelect={handleCompanySelect}
              onFundFilterChange={handleFundFilterChange}
              onSearchChange={handleCompanySearchChange}
              searchValue={companySearchValue}
              selectedCompanyId={selectedCompanyId}
              selectedFundId={selectedFundId}
            />
          )}
          minCenterWidth={FIRM_DOCUMENTS_PANE_WIDTHS.minDocumentsListWidth}
          minLeftWidth={FIRM_DOCUMENTS_PANE_WIDTHS.minCompanyListWidth}
          testIds={{
            centerPane: FIRM_DOCUMENTS_TEST_IDS.centerPane,
            leftPane: FIRM_DOCUMENTS_TEST_IDS.companyPane,
            leftResizeHandle: FIRM_DOCUMENTS_TEST_IDS.leftResizeHandle,
            root: FIRM_DOCUMENTS_TEST_IDS.workspaceLayout,
          }}
        />
        <PrimaryNavigationMenu
          anchorEl={bulkActionsAnchorEl}
          data-testid={FIRM_DOCUMENTS_TEST_IDS.bulkActionsMenu}
          menuMinWidth={0}
          menuWidth="content"
          onClose={handleBulkActionsClose}
          open={Boolean(bulkActionsAnchorEl) && selectedDocumentCount > 0}>
          {canDownloadDocuments ? (
            <PrimaryNavigationMenuItem
              data-testid={FIRM_DOCUMENTS_TEST_IDS.bulkActionsDownload}
              disabled={selectedDocumentCount === 0 || isDocumentActionInProgress}
              leadingSlot={<CloudDownloadOutlined fontSize="inherit" />}
              onClick={handleBulkDownload}>
              {FIRM_DOCUMENTS_LABELS.bulkDownload}
            </PrimaryNavigationMenuItem>
          ) : null}
          {canMutateDocuments && isCompanyDocumentsView ? [
            <PrimaryNavigationMenuItem
              key="copy"
              data-testid={FIRM_DOCUMENTS_TEST_IDS.bulkActionsCopy}
              disabled={selectedDocumentCount === 0 || isDocumentActionInProgress}
              leadingSlot={<ContentCopyOutlined fontSize="inherit" />}
              onClick={handleBulkCopy}>
              {FIRM_DOCUMENTS_LABELS.bulkCopy}
            </PrimaryNavigationMenuItem>,
            <PrimaryNavigationMenuItem
              key="analyze-cap-table"
              data-testid={FIRM_DOCUMENTS_TEST_IDS.bulkActionsAnalyzeCapTable}
              disabled={selectedDocumentCount === 0 || isDocumentActionInProgress}
              leadingSlot={<PrimaryNavigationMenuAiIcon />}
              onClick={() => handleBulkAnalyze(ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE)}
              tone="ai">
              {FIRM_DOCUMENTS_LABELS.bulkAnalyzeCapTable}
            </PrimaryNavigationMenuItem>,
            <PrimaryNavigationMenuItem
              key="analyze-financials"
              data-testid={FIRM_DOCUMENTS_TEST_IDS.bulkActionsAnalyzeFinancials}
              disabled={selectedDocumentCount === 0 || isDocumentActionInProgress}
              leadingSlot={<PrimaryNavigationMenuAiIcon />}
              onClick={() => handleBulkAnalyze(ApiCreateDocumentGenerationJob.processing_type.FINANCIAL_MAPPING)}
              tone="ai">
              {FIRM_DOCUMENTS_LABELS.bulkAnalyzeFinancials}
            </PrimaryNavigationMenuItem>,
          ] : null}
          {canMutateDocuments ? [
            shouldShowBulkDeleteDivider ? <Divider key="delete-divider" /> : null,
            <PrimaryNavigationMenuItem
              key="delete"
              data-testid={FIRM_DOCUMENTS_TEST_IDS.bulkActionsDelete}
              disabled={selectedDocumentCount === 0 || isDocumentActionInProgress}
              leadingSlot={<DeleteOutline fontSize="inherit" />}
              onClick={handleBulkDelete}
              tone="danger">
              {FIRM_DOCUMENTS_LABELS.bulkDelete}
            </PrimaryNavigationMenuItem>,
          ] : null}
        </PrimaryNavigationMenu>
        <FirmDocumentAnalyzeDialog
          actionLabel={pendingAnalyzeActionLabel}
          documentCount={visiblePendingAnalyzeAction?.documentReferences.length ?? 0}
          fileNames={pendingAnalyzeDocumentFileNames}
          isAnalyzing={isAnalyzingDocuments}
          onClose={handleAnalyzeDialogClose}
          onConfirm={handleAnalyzeConfirm}
          open={isAnalyzeDialogOpen}
        />
        <FirmDocumentCopyDialog
          documentCount={visibleCopyingDocumentReferences.length}
          errorMessage={copyDialogErrorMessage}
          initialTargetMeasurementDateId={initialCopyTargetMeasurementDateId}
          isCopying={isCopyingDocumentReferences}
          isMeasurementDatesLoading={isCopyTargetMeasurementDatesLoading}
          measurementDates={copyTargetMeasurementDates}
          onClose={handleCopyDialogClose}
          onConfirm={handleCopyConfirm}
          onErrorClear={() => setCopyDialogErrorMessage(null)}
          open={isCopyDialogOpen}
          sourceMeasurementDateId={selectedMeasurementDateId}
        />
        <FirmDocumentMoveDialog
          errorMessage={moveDialogErrorMessage}
          fileName={pendingMoveDocumentFileName}
          isMoving={isMovingDocumentReferences}
          onClose={handleMoveDialogClose}
          onConfirm={handleMoveConfirm}
          open={isMoveDialogOpen}
          sourceLabel={visiblePendingMoveAction
            ? getDocumentReferenceCompanyName(visiblePendingMoveAction.documentReference)
            : null}
          targetLabel={visiblePendingMoveAction?.targetLabel}
        />
        <FirmDocumentDownloadDialog
          documentCount={visibleDownloadingDocumentReferences.length}
          fileNames={downloadingDocumentFileNames}
          isDownloading={isDownloadingDocuments}
          onClose={handleDownloadDialogClose}
          onConfirm={handleDownloadConfirm}
          open={isDownloadDialogOpen}
        />
        <FirmDocumentsFilterDialog
          filterOptions={firmDocumentFilterOptionsQuery.data}
          isLoading={firmDocumentFilterOptionsQuery.isLoading}
          onApply={handleDocumentFilterApply}
          onClose={() => setIsFilterDialogOpen(false)}
          open={isFilterDialogOpen}
          value={documentFilterValue}
        />
        <FirmDocumentDeleteDialog
          documentCount={visibleDeletingDocumentReferences.length}
          fileName={
            visibleDeletingDocumentReferences.length === 1
              ? getFirmDocumentReferenceFileName(visibleDeletingDocumentReferences[0])
              : undefined
          }
          fileNames={deletingDocumentFileNames}
          isDeleting={isDeletingDocumentReferences}
          onClose={handleDeleteDialogClose}
          onConfirm={handleDeleteConfirm}
          open={isDeleteDialogOpen}
        />
        <FirmDocumentEditDialog
          documentReference={visibleEditingDocumentReference}
          isLoadingRequestedFiles={firmDocumentRequestedFilesQuery.isLoading}
          isSaving={isEditingDocumentReference}
          onClose={handleDocumentEditDialogClose}
          onConfirm={handleDocumentEditConfirm}
          open={isDocumentEditDialogOpen}
          referenceOptions={FIRM_DOCUMENTS_SUPPORTED_REFERENCE_OPTIONS}
          requestedFileOptions={visibleRequestedFileOptions}
        />
        <FirmDocumentFolderDeleteDialog
          folderName={visibleDeletingFolder?.folderName}
          isDeleting={isDeletingFolder}
          onClose={handleFolderDeleteDialogClose}
          onConfirm={handleFolderDeleteConfirm}
          open={isFolderDeleteDialogOpen}
        />
        <FirmDocumentFolderEditDialog
          currentParentFolderId={visibleEditingFolder?.currentParentFolderId}
          folderName={visibleEditingFolder?.folderName}
          isSaving={isRenamingFolder}
          mdDocumentsId={visibleEditingFolder?.mdDocumentsId}
          onClose={handleFolderEditDialogClose}
          onConfirm={handleFolderEditConfirm}
          open={isFolderEditDialogOpen}
          parentFolderOptions={visibleEditingFolder?.parentFolderOptions ?? []}
          siblingFolderNamesByTargetKey={visibleEditingFolder?.siblingFolderNamesByTargetKey}
        />
        <FirmDocumentFolderCreateDialog
          companyOptions={visibleCreatingFolder?.companyOptions}
          initialCompanyId={visibleCreatingFolder?.initialCompanyId}
          initialParentFolderId={visibleCreatingFolder?.initialParentFolderId}
          isCreating={isCreatingFolder}
          measurementDateLabel={visibleCreatingFolder?.measurementDateLabel}
          mdDocumentsId={visibleCreatingFolder?.mdDocumentsId}
          onClose={handleFolderCreateDialogClose}
          onConfirm={handleFolderCreateConfirm}
          open={isFolderCreateDialogOpen}
          parentFolderOptions={visibleCreatingFolder?.parentFolderOptions}
          parentFolderOptionsByCompanyId={visibleCreatingFolder?.parentFolderOptionsByCompanyId}
          parentFolderName={visibleCreatingFolder?.parentFolderName}
          siblingFolderNames={visibleCreatingFolder?.siblingFolderNames}
          siblingFolderNamesByTargetKey={visibleCreatingFolder?.siblingFolderNamesByTargetKey}
          targetCompanyName={visibleCreatingFolder?.targetCompanyName}
        />
        <FirmDocumentUploadDialog
          autoCloseSeconds={isUploadComplete ? uploadAutoCloseSeconds : null}
          companyOptions={companyMetadata}
          firmId={firmId}
          initialCompanyId={visibleUploadingDocumentContext?.initialCompanyId}
          initialFolderId={visibleUploadingDocumentContext?.initialFolderId}
          initialMeasurementDateDate={visibleUploadingDocumentContext?.initialMeasurementDateDate}
          initialMeasurementDateId={visibleUploadingDocumentContext?.initialMeasurementDateId}
          isCompanyLocked={visibleUploadingDocumentContext?.isCompanyLocked}
          isUploadComplete={isUploadComplete}
          isUploading={isUploadDialogBusy}
          onClose={handleUploadDialogClose}
          onConfirm={handleUploadConfirm}
          open={isUploadDialogOpen}
          uploadProgress={uploadProgress}
        />
      </FirmDocumentsRoot>
    </PrismThemeProvider>
  );
};

export default FirmDocuments;
